import React from 'react';
import {
  ChevronLeft,
  Copy,
  Download,
  Mic,
  Pencil,
  Play,
  Plus,
  Printer,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { getTimeElapsed } from '@/lib/utils/timeElapsed';
import { getInitials } from '@/lib/utils/avatar';
import { Button } from '@/components/base/Button';
import { LabReportsSection } from '@/components/consultation/LabReportsSection';
import { cn } from '@/lib/utils/cn';
import { generateDiagnosticReportsPDF } from '@/lib/utils/pdfGenerator';
import { stripHtml } from '@/lib/api/medplum-resources';
import {
  commaTextToHtmlList,
  familyHistoryTextToHtmlList,
  formatDiagnosisEntry,
  formatInvestigationEntry,
  formatRelationConditionEntry,
  formatRelationConditionList,
  isRelationConditionItem,
  labelValueTextToHtmlList,
  sanitizeClinicalHtml,
  splitCommaSeparatedText,
  proseTextToHtmlList,
  splitMedicalNarrativeToBullets,
  splitProseToBullets,
  summaryBulletsToHtmlList,
} from '@/lib/utils/clinicalText';
import {
  formatVitals,
  type AllergyConflictAlert,
  type PranikCDSS,
  type VitalSigns,
} from '@/lib/types/consultation';
import {
  toPrescribedMedicine,
  toStructuredMedication,
  type StructuredMedication,
} from '@/lib/utils/medications';
import type { PrescribedMedicine } from '@/ayurveda/types/ayurveda';
import { printPrescription, type PrescriptionData } from '@/lib/utils/prescriptionGenerator';
import { useAuthStore } from '@/lib/stores/authStore';
import { VISIT_TABS, type VisitTab } from '@/lib/utils/patientVisits';
import { MedicinesTable } from '@/components/consultation/paData/TreatmentCard';
import { CDSSMedicationCard } from '@/components/consultation/CDSSMedicationCard';

type MedicationEntry = {
  name: string;
  dosage: string;
  frequency: string;
  route: string;
  duration: string;
  relationWithMeal: string;
  comments: string;
  itemCode: string;
  medicineType: string;
  /** e.g. "Amoxicillin + Clavulanic acid" — the medication's composition/salt breakdown. */
  composition: string;
  /** e.g. "Morning, Night" — when during the day each dose is taken. */
  timing: string;
  /**
   * Position of this medication in the saved summary's list. Sent back on save so the
   * original entry (and fields the table doesn't show, e.g. dosing_type / sos_trigger /
   * max_per_day) can be merged with the edited columns instead of being rebuilt.
   */
  originalIndex?: number;
};

type SummarySection = {
  label: string;
  content: string;
  /** When set, the section contains structured medication data to render as a table. */
  medications?: MedicationEntry[];
  /** The raw JSON key `medications` was read from — needed to write edits back in place. */
  medicationsSourceKey?: string;
  /** When set, the section contains ICD-11 diagnosis codes to render as a table. */
  diagnosisCodes?: Array<{ symptom: string; code: string; description: string; type: string }>;
  /**
   * The individual editable text pieces that make up `content`, each tagged with the
   * raw JSON key it came from. A section can combine several underlying fields (e.g.
   * "Other History" merges clinicalSymptoms + medicalHistory) — editing happens per
   * field so saving never collapses distinct fields into one, or drops the others.
   * Absent for sections with no known writable key (e.g. diagnosisCodes).
   */
  fields?: Array<{ label: string; content: string; sourceKey: string }>;
};

/** Shared styling for every edit-mode textarea across the visit/vitals/investigations editors. */
const EDIT_TEXTAREA_CLASS =
  'w-full rounded-lg border border-[#E6E8EF] bg-white px-4 py-3 text-lg leading-8 text-[#051858] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6B8AF9]/40';

/** Shared styling for every edit-mode single-line input (medication rows, vitals fields). */
const EDIT_INPUT_CLASS =
  'rounded-lg border border-[#E6E8EF] bg-white px-3 py-2.5 text-base text-[#051858] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6B8AF9]/40';

/** The numeric vitals editable in the Vitals card, in display order. */
const NUMERIC_VITAL_FIELDS: Array<{ key: keyof VitalSigns; label: string; unit: string }> = [
  { key: 'heartRate', label: 'Heart Rate', unit: 'bpm' },
  { key: 'temperature', label: 'Temperature', unit: '°F' },
  { key: 'respiratoryRate', label: 'Respiratory Rate', unit: 'breaths/min' },
  { key: 'oxygenSaturation', label: 'SpO2', unit: '%' },
  { key: 'bloodSugar', label: 'Blood Sugar', unit: 'mg/dL' },
  { key: 'weight', label: 'Weight', unit: 'kg' },
  { key: 'height', label: 'Height', unit: 'cm' },
];

/**
 * The API has historically returned summary fields in several shapes:
 * strings, { value }, arrays of objects, and nested objects. Keep the
 * conversion in one place so the records page never falls back to
 * JavaScript's "[object Object]" representation.
 */
export function formatSummaryValue(value: unknown, keyHint = ''): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }

  if (Array.isArray(value)) {
    // Family-history-style entries render as numbered, labeled blocks instead
    // of a comma-joined sentence — see formatRelationConditionList.
    if (value.length > 0 && value.every(isRelationConditionItem)) {
      return formatRelationConditionList(value);
    }
    return value
      .map((item) => formatSummaryValue(item, keyHint))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    if ('test' in objectValue) {
      return formatInvestigationEntry(objectValue);
    }
    if ('value' in objectValue) {
      const val = formatSummaryValue(objectValue.value, keyHint);
      const unit = 'unit' in objectValue ? formatSummaryValue(objectValue.unit, keyHint) : '';
      return unit ? `${val} ${unit}` : val;
    }

    if ('relation' in objectValue && 'condition' in objectValue) {
      return formatRelationConditionEntry(objectValue);
    }
    if ('diagnosis' in objectValue) {
      return formatDiagnosisEntry(objectValue as { diagnosis: unknown; notes?: unknown });
    }
    if ('type' in objectValue && 'value' in objectValue) {
      const t = formatSummaryValue(objectValue.type);
      const v = formatSummaryValue(objectValue.value);
      if (t && v) return `${t}: ${v}`;
      return t || v;
    }
    const isMedication =
      'medication' in objectValue ||
      ('name' in objectValue &&
        (keyHint.toLowerCase().includes('medication') ||
          'dosage' in objectValue ||
          'frequency' in objectValue ||
          'period' in objectValue ||
          'duration' in objectValue));
    if (isMedication) {
      const name = formatSummaryValue(objectValue.medication ?? objectValue.name);
      const dosage = formatSummaryValue(objectValue.dosage);
      const frequency = formatSummaryValue(objectValue.frequency ?? objectValue.period);
      const duration = formatSummaryValue(objectValue.duration);
      return [name, dosage, frequency, duration].filter(Boolean).join(' | ');
    }

    return Object.entries(objectValue)
      .map(([key, nestedValue]) => {
        const formatted = formatSummaryValue(nestedValue, key);
        return formatted ? `${toSummaryLabel(key)}: ${formatted}` : '';
      })
      .filter(Boolean)
      .join('; ');
  }

  return '';
}

/**
 * Convert a section's stored HTML (e.g. "<b>Severe cold</b>, fever") into plain text
 * a doctor can edit in a textarea, representing bold and italic as markdown-style
 * asterisk markers instead of dropping the formatting outright.
 */
function htmlToEditableText(html: string): string {
  const withLineBreaks = html.replace(/<\/(li|p|div)>|<br\s*\/?>/gi, '\n');
  const doc = new DOMParser().parseFromString(withLineBreaks, 'text/html');
  doc.querySelectorAll('b, strong').forEach((el) => {
    el.replaceWith(doc.createTextNode(`**${el.textContent || ''}**`));
  });
  doc.querySelectorAll('i, em').forEach((el) => {
    el.replaceWith(doc.createTextNode(`*${el.textContent || ''}*`));
  });
  const text = doc.body.textContent || '';
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

/**
 * Convert a section's stored HTML (built by consumeAsBulletHtml — <p><strong>
 * labels, <ul><li> bullets, <br> line breaks within an entry, e.g. a family
 * history block's Relation/Condition/Notes lines) into readable plain text
 * for the Copy/Download summary — "• " per bullet, each entry's internal
 * lines indented, and a bold heading rendered as its own line (markdown
 * dropped entirely here since plain text has no bold). Mirrors stripHtml's
 * tag-to-newline approach but adds the bullet marker stripHtml doesn't.
 */
function htmlSectionToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('br').forEach((el) => {
    el.replaceWith(doc.createTextNode('\n  '));
  });
  doc.querySelectorAll('li').forEach((el) => {
    el.insertBefore(doc.createTextNode('• '), el.firstChild);
    el.appendChild(doc.createTextNode('\n\n'));
  });
  doc.querySelectorAll('p, div').forEach((el) => {
    el.appendChild(doc.createTextNode('\n'));
  });
  const text = doc.body.textContent || '';
  return text
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Matches a section-header line inside patient.visitSummary's flat narrative
 * — a short line ending in ":" with nothing else on it (e.g. "Patient
 * Summary:", "Allergies:", "Chief Complaint:"). Deliberately requires the
 * WHOLE trimmed line to match (anchored start/end), so a content sentence
 * that happens to end in a colon mid-way through a longer line never
 * false-positives — those are always full ALL-CAPS sentences ending in a
 * period, not a bare "Label:" line.
 */
const VISIT_SUMMARY_HEADER_LINE = /^([A-Za-z][A-Za-z &'/-]{1,40}):\s*$/;

/**
 * patient.visitSummary (the legacy "AI paragraph" fallback shown when there's
 * no structured departmentConfig) is a single flat string the backend writes
 * directly — section labels and their bullet-per-line content all run
 * together with blank-line separators, e.g. "Patient Summary:\n\nline one\n
 * line two\n\nAllergies:\n\nline one". It never passes through
 * consumeAsBulletHtml/buildSummarySections at all, so none of the bullet
 * fixes there apply to it. Parses it into the same bold-heading + bullet-list
 * shape as the structured path: a line matching VISIT_SUMMARY_HEADER_LINE
 * becomes its own bold heading, and every other non-blank line until the next
 * heading becomes its own bullet.
 */
function formatVisitSummaryText(text: string): string {
  const parts: string[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length > 0) {
      parts.push(`<ul>${bullets.map((b) => `<li>${b}</li>`).join('')}</ul>`);
      bullets = [];
    }
  };

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const headerMatch = line.match(VISIT_SUMMARY_HEADER_LINE);
    if (headerMatch) {
      flushBullets();
      parts.push(`<p><strong>${headerMatch[1]}:</strong></p>`);
    } else {
      bullets.push(line);
    }
  }
  flushBullets();
  return parts.join('');
}

/**
 * Reverse of htmlToEditableText — turns edited plain text (with markdown-style bold
 * and italic markers a doctor typed) back into the HTML the read-only view expects,
 * escaping everything else so edited text can't inject markup.
 */
function editableTextToHtml(text: string): string {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/\*(.+?)\*/g, '<i>$1</i>');
}

function toSummaryLabel(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const fullCaseSheetFieldOrder = [
  'patientSummary',
  'chiefComplaint',
  'clinicalSymptoms',
  'medicalHistory',
  'familyHistory',
  'socialHistory',
  'reviewOfSystems',
  'allergies',
  'generalExamination',
  'vitals',
  'assessmentDiagnosis',
  'investigations',
  'medicationHistory',
  'medications',
  'treatmentPlan',
  'dietAdvice',
  'followUpPlan',
  'doctorNotes',
  'mealPlan',
  'pediatricMilestone',
  'doshaProfile',
  'diet',
  'lifestyle',
  'viruddha-ahaara',
];

const summaryFieldLabels: Record<string, string> = {
  patientSummary: 'Patient Summary',
  chiefComplaint: 'Chief Complaint',
  clinicalSymptoms: 'Clinical Symptoms',
  medicalHistory: 'Medical History',
  familyHistory: 'Family History',
  socialHistory: 'Social History',
  reviewOfSystems: 'Review of Systems',
  allergies: 'Allergies',
  generalExamination: 'General Examination',
  vitals: 'Vitals',
  assessmentDiagnosis: 'Assessment & Diagnosis',
  investigations: 'Investigations',
  medicationHistory: 'Medication History',
  medications: 'Medications',
  prescribedMedications: 'Medication',
  treatmentPlan: 'Treatment Plan',
  dietAdvice: 'Diet Advice',
  followUpPlan: 'Follow Up Plan',
  doctorNotes: "Doctor's Notes",
  mealPlan: 'Meal Plan',
  adtOrder: 'ADT Orders',
  pediatricMilestone: 'Pediatric Milestone',
  doshaProfile: 'Astha Sthaana Pareeksha',
  diet: 'Diet',
  lifestyle: 'Lifestyle',
  'viruddha-ahaara': 'Viruddha Ahaara',
};

// Fields that should be omitted entirely when empty, rather than always rendering a
// "no data" placeholder like most fields do. Three reasons a field ends up here:
//  - it already has its own dedicated always-shown widget elsewhere on the page
//    (Vitals card, Investigations card), so a placeholder here would just duplicate it;
//  - it's a pediatrics/Ayurveda-only field (pediatricMilestone, doshaProfile, diet,
//    lifestyle, viruddha-ahaara) that shouldn't clutter Patient Record for departments
//    that don't use it;
//  - it's clinicalSymptoms, which shouldn't clutter the "Other History" group with a
//    "not recorded" line when there's no real content.
const fieldsHiddenWhenEmpty = new Set([
  'vitals',
  'investigations',
  'mealPlan',
  'pediatricMilestone',
  'doshaProfile',
  'diet',
  'lifestyle',
  'viruddha-ahaara',
  'clinicalSymptoms',
]);

// Mirrors CaseSheetView's emptyMessages — every configured field should render even
// with no data, using this text instead of being silently omitted.
const summaryEmptyMessages: Record<string, string> = {
  patientSummary: 'No patient summary recorded',
  chiefComplaint: 'No chief complaint recorded',
  clinicalSymptoms: 'No clinical symptoms recorded',
  medicalHistory: 'No medical history recorded',
  familyHistory: 'No family history recorded',
  socialHistory: 'No social history recorded',
  reviewOfSystems: 'No review of systems recorded',
  allergies: 'No known allergies',
  generalExamination: 'No examination findings recorded',
  assessmentDiagnosis: 'No assessment or diagnosis recorded',
  investigations: 'No investigations recorded',
  medicationHistory: 'No drug history recorded',
  medications: 'No prescribed medications recorded',
  prescribedMedications: 'No prescribed medications recorded',
  treatmentPlan: 'No treatment plan recorded',
  dietAdvice: 'No diet advice recorded',
  followUpPlan: 'No follow up plan recorded',
  doctorNotes: 'No additional notes recorded',
  adtOrder: 'No ADT orders recorded',
};

function getSummaryEmptyMessage(field: string, label: string): string {
  return summaryEmptyMessages[field] || `No ${label.toLowerCase()} recorded`;
}

const summaryFieldAliases: Record<string, string[]> = {
  patientSummary: ['patientSummary', 'patient_summary', 'summary'],
  chiefComplaint: ['chiefComplaint', 'chief_complaint'],
  clinicalSymptoms: ['clinicalSymptoms', 'history_of_present_illness', 'clinical_symptoms'],
  medicalHistory: ['medicalHistory', 'past_medical_surgical_history', 'medical_history'],
  familyHistory: ['familyHistory', 'family_history'],
  socialHistory: ['socialHistory', 'social_history'],
  reviewOfSystems: ['reviewOfSystems', 'review_of_systems'],
  allergies: ['allergies'],
  generalExamination: [
    'generalExamination',
    'general_examination',
    'physical_examination',
    'examination',
  ],
  vitals: ['vitals', 'vital_signs'],
  assessmentDiagnosis: [
    'assessmentDiagnosis',
    'assessment_and_diagnosis',
    'assessment',
    'diagnosis',
  ],
  investigations: ['investigations', 'investigations_orders', 'investigationsOrders'],
  medicationHistory: ['medicationHistory', 'medication_history'],
  medications: ['medications', 'prescribedMedications', 'prescribed_medications'],
  prescribedMedications: ['prescribedMedications', 'medications', 'prescribed_medications'],
  treatmentPlan: ['treatmentPlan', 'treatment_plan_management', 'treatment_plan', 'plan'],
  dietAdvice: ['dietAdvice', 'diet_advice'],
  followUpPlan: ['followUpPlan', 'follow_up_plan', 'follow_up_details'],
  doctorNotes: ['doctorNotes', 'doctor_notes'],
  mealPlan: ['mealPlan', 'meal_plan'],
  adtOrder: ['adtOrder', 'adt_order'],
  pediatricMilestone: ['pediatricMilestone', 'pediatric_milestone'],
  doshaProfile: ['doshaProfile', 'dosha_profile'],
  diet: ['diet', 'diet_recommendations'],
  lifestyle: ['lifestyle', 'lifestyle_recommendations'],
  'viruddha-ahaara': ['viruddha-ahaara', 'viruddha_ahaara'],
};

function findSummaryValue(source: Record<string, unknown>, field: string): unknown {
  const aliases = summaryFieldAliases[field] || [field];
  return aliases.map((alias) => source[alias]).find((value) => formatSummaryValue(value));
}

/**
 * Map a Composition's raw sections (free-form titles the AI Physician Assistant
 * invents per visit) directly into display sections, one per section, in
 * original order — bypassing the case-sheet field-matching pipeline entirely,
 * since these titles don't correspond to any known case-sheet field.
 */
export function mapCompositionSections(sections: unknown): SummarySection[] | null {
  if (!Array.isArray(sections)) return null;
  const result = sections
    .map((section): SummarySection | null => {
      const s = section as { title?: unknown; content?: unknown };
      const label = typeof s.title === 'string' ? s.title : '';
      // Reference Slokas are live-consultation aids, not part of the visit record.
      if (/^reference\s+slokas?$/i.test(label.trim())) return null;
      const content = typeof s.content === 'string' ? stripHtml(s.content) : '';
      // sourceKey = the section title, which is how a saved edit is matched back to
      // its Composition section (see medplumResourcesApi.updateComposition).
      return label && content
        ? { label, content, fields: [{ label, content: s.content as string, sourceKey: label }] }
        : null;
    })
    .filter((s): s is SummarySection => s !== null);
  return result.length > 0 ? result : null;
}

function unwrapSummarySource(source: Record<string, unknown>): Record<string, unknown> {
  for (const wrapperKey of ['summary', 'data', 'result']) {
    const wrapped = source[wrapperKey];
    if (
      wrapped &&
      typeof wrapped === 'object' &&
      !Array.isArray(wrapped) &&
      !('value' in wrapped)
    ) {
      // Exclude the wrapper key itself to avoid it appearing as a duplicate section.
      const rest = Object.fromEntries(Object.entries(source).filter(([k]) => k !== wrapperKey));
      return { ...(wrapped as Record<string, unknown>), ...rest };
    }
  }
  return source;
}

const ignoredSummaryKeys = new Set([
  'pranik_cdss',
  'pranikCdss',
  'savedAt',
  'saved_at',
  'saveAt',
  'save_at',
  'autoSaved',
  'auto_saved',
  'lastUpdated',
  'last_updated',
  'updatedAt',
  'updated_at',
  'createdAt',
  'created_at',
  'schema_version',
  'schemaVersion',
  'cdss_disclaimer',
  'cdssDisclaimer',
  // Reference Slokas are live-consultation aids, not part of the visit record.
  'reference_slokas',
  'referenceSlokas',
  'suggestive_slokas',
  'suggestiveSlokas',
]);

function buildSummarySections(
  source: Record<string, unknown> | null,
  departmentConfig:
    { fieldOrder: string[]; fieldGroups?: Record<string, string[]> } | null | undefined,
  isAyurveda?: boolean
): SummarySection[] | null {
  if (!source) return null;

  source = unwrapSummarySource(source);

  const sections: SummarySection[] = [];
  const consumedKeys = new Set<string>();
  // Ayurveda-only: 'personal_history' has no summaryFieldAliases entry, so it
  // would otherwise fall through to the unrecognized-key fallback below and
  // render as a duplicate generic "Personal History" text section — the
  // dedicated Personal History card elsewhere on this page already reads
  // this exact same source key.
  // Ayurveda-only: 'treatment' has no summaryFieldAliases entry either, and
  // would otherwise render as a raw, unstructured duplicate of the dedicated
  // Treatment card (which has a real medicines table + treatment principle).
  if (isAyurveda) {
    consumedKeys.add('personal_history');
    consumedKeys.add('personalHistory');
    consumedKeys.add('treatment');
    consumedKeys.add('treatments');
  }
  // When the project has a real departmentConfig, follow it exactly — fields the
  // department left out of fieldOrder (e.g. Social History, Medication History)
  // must not reappear here just because they're in the generic default order.
  // Only fall back to the full default order when no departmentConfig exists at
  // all. (A field with real, unconsumed data still survives via the "preserve
  // fields introduced by backend/config" fallback loop below, so this can't
  // silently drop actual clinical content — only empty placeholder sections for
  // fields the department didn't configure.)
  // Patient Summary always shows on Patient Record, regardless of whether the
  // active department's fieldOrder lists it — unlike every other field, it
  // isn't something a department should be able to configure away here. Case
  // Sheet intentionally does NOT get this same treatment — only Patient
  // Record forces it in.
  const baseOrder = departmentConfig?.fieldOrder?.length
    ? [...new Set(departmentConfig.fieldOrder)]
    : fullCaseSheetFieldOrder;
  const order = baseOrder.includes('patientSummary') ? baseOrder : ['patientSummary', ...baseOrder];

  // When following a real departmentConfig, a *known* field the department left out
  // of fieldOrder (e.g. Medications, Doctor's Notes) must stay hidden even when it has
  // real data — otherwise it silently reappears via the "preserve fields introduced by
  // backend/config" fallback loop below, defeating the fieldOrder restriction above.
  // Only pre-consume fields that are recognized (have a summaryFieldAliases entry) and
  // aren't reachable through `order` directly or nested inside a fieldGroups parent —
  // a genuinely unrecognized key (no alias entry at all) still falls through to that
  // fallback loop as intended, so real new fields are never silently dropped.
  if (departmentConfig?.fieldOrder?.length) {
    const reachableFields = new Set([
      ...order,
      ...Object.values(departmentConfig.fieldGroups || {}).flat(),
    ]);
    for (const [field, aliases] of Object.entries(summaryFieldAliases)) {
      if (!reachableFields.has(field)) {
        aliases.forEach((key) => consumedKeys.add(key));
      }
    }
  }

  const consume = (field: string): string => {
    const aliases = summaryFieldAliases[field] || [field];
    aliases.forEach((key) => consumedKeys.add(key));
    return formatSummaryValue(findSummaryValue(source, field), field);
  };

  /**
   * Render a field's content as HTML, preferring a direct per-array-item
   * bullet list (summaryBulletsToHtmlList) when the raw value is already an
   * array — the modern backend schema's native shape for nearly every text
   * field. Falls back to the legacy flatten-then-resplit helpers
   * (commaTextToHtmlList / labelValueTextToHtmlList / familyHistoryTextToHtmlList)
   * for fields still arriving as a single pre-joined string from older
   * sessions, so neither payload shape regresses.
   */
  const consumeAsBulletHtml = (field: string): string => {
    const rawValue = findSummaryValue(source, field);
    const rawContent = consume(field);

    // Medical History must run before the generic array short-circuit below:
    // it needs to extract an embedded "Lab investigations reviewed:" item
    // into its own sub-section even when rawValue is a genuine array (the
    // modern schema's native shape) — summaryBulletsToHtmlList has no concept
    // of that sub-section, so letting it return early here would merge lab
    // values straight into the main Medical History list for fresh data,
    // while the PDF (which doesn't have this early-return) correctly splits
    // them. Builds a '\n'-joined string from the raw array items (not a
    // comma-join) so splitMedicalNarrativeToBullets trusts each item as its
    // own authoritative line, same rationale as extractSessionField/
    // getSessionFieldValue/getFieldValue.
    if (field === 'medicalHistory') {
      const unwrapped =
        rawValue &&
        typeof rawValue === 'object' &&
        !Array.isArray(rawValue) &&
        'value' in (rawValue as object)
          ? (rawValue as { value?: unknown }).value
          : rawValue;
      const medHistoryText = Array.isArray(unwrapped)
        ? unwrapped
            .map((item) => (typeof item === 'string' ? item : formatSummaryValue(item)))
            .filter(Boolean)
            .join('\n')
        : rawContent;
      const { mainItems, labItems } = splitMedicalNarrativeToBullets(medHistoryText);
      if (mainItems.length === 0 && labItems.length === 0) return rawContent;
      const mainHtml =
        mainItems.length > 0 ? `<ul>${mainItems.map((i) => `<li>${i}</li>`).join('')}</ul>` : '';
      const labHtml =
        labItems.length > 0
          ? `<p><strong>Lab Investigations Reviewed:</strong></p><ul>${labItems.map((i) => `<li>${i}</li>`).join('')}</ul>`
          : '';
      return `${mainHtml}${labHtml}`;
    }

    // Chief Complaint and Allergies always render every bullet bold, regardless
    // of whether the backend happened to highlight that particular item with <b>.
    const forceBold = field === 'chiefComplaint' || field === 'allergies';
    const bulletHtml = summaryBulletsToHtmlList(rawValue, { forceBold });
    if (bulletHtml) return bulletHtml;
    if (!rawContent) return '';
    if (forceBold) {
      const items = splitCommaSeparatedText(rawContent);
      return items.length > 0
        ? `<ul>${items.map((i) => `<li><b>${i}</b></li>`).join('')}</ul>`
        : rawContent;
    }
    if (field === 'assessmentDiagnosis') {
      return commaTextToHtmlList(rawContent) || rawContent;
    }
    if (field === 'generalExamination') {
      return labelValueTextToHtmlList(rawContent) || rawContent;
    }
    if (field === 'familyHistory') {
      return familyHistoryTextToHtmlList(rawContent) || rawContent;
    }
    // Every other legacy plain-string field (Social History, Review of
    // Systems, Treatment Plan, Doctor's Notes, Diet Advice, Follow Up Plan,
    // Pediatric Milestone, etc.) renders as bullets too — one per
    // sentence/list-comma. The modern array schema is already fully bulleted
    // via summaryBulletsToHtmlList above; this only matters for older stored
    // sessions still holding a single joined string.
    return proseTextToHtmlList(rawContent) || rawContent;
  };

  // The raw JSON key a field's value actually lives under (first alias with content,
  // else the canonical alias) — needed so edits can be written back to the exact key.
  const resolveKey = (field: string): string => {
    const aliases = summaryFieldAliases[field] || [field];
    const found = aliases.find((alias) => formatSummaryValue(source[alias]));
    return found || aliases[0];
  };

  // Helper to extract structured medications from source
  const extractMedications = (field: string): MedicationEntry[] => {
    const raw = findSummaryValue(source, field);
    if (!raw) return [];
    const list =
      typeof raw === 'object' && raw !== null && 'value' in (raw as object)
        ? (raw as { value: unknown }).value
        : raw;
    const items = Array.isArray(list) ? list : [list];
    return items
      .map((item: unknown, itemIndex) => {
        if (!item) return null;
        if (typeof item === 'string') {
          const trimmed = item.trim();
          if (!trimmed) return null;
          return {
            name: trimmed,
            dosage: '',
            frequency: '',
            route: '',
            duration: '',
            relationWithMeal: '',
            comments: '',
            itemCode: '',
            medicineType: '',
            composition: '',
            timing: '',
          };
        }
        if (typeof item === 'object') {
          const obj = item as Record<string, unknown>;
          const name = formatSummaryValue(obj.medication ?? obj.name ?? obj.drug_name) || '';
          const dosage = formatSummaryValue(obj.dosage ?? obj.dose ?? obj.strength) || '';
          const unit = formatSummaryValue(obj.unit) || '';
          const frequency = formatSummaryValue(obj.frequency ?? obj.period ?? obj.schedule) || '';
          const route = formatSummaryValue(obj.route ?? obj.intake_type) || '';
          const duration = formatSummaryValue(obj.duration ?? obj.days) || '';
          const relationWithMeal =
            formatSummaryValue(obj.relation_with_meal ?? obj.relationWithMeal) || '';
          const itemCode = formatSummaryValue(obj.item_code ?? obj.itemCode) || '';
          const medicineType = formatSummaryValue(obj.medicine_type ?? obj.medicineType) || '';
          const composition = formatSummaryValue(obj.composition) || '';
          const timing = formatSummaryValue(obj.timing) || '';

          let comments =
            formatSummaryValue(obj.additional_info ?? obj.additionalInfo ?? obj.comments) || '';
          // SOS (as-needed) medications carry their trigger/dosing-cap in separate
          // fields rather than the fixed frequency/duration a scheduled medication
          // has — fold them into Comments so the table still surfaces them.
          const dosingType = formatSummaryValue(obj.dosing_type) || '';
          if (dosingType.toLowerCase() === 'sos') {
            const sosTrigger = formatSummaryValue(obj.sos_trigger) || '';
            const maxPerDay = formatSummaryValue(obj.max_per_day) || '';
            const sosParts = [
              sosTrigger ? `if ${sosTrigger}` : '',
              maxPerDay ? `max ${maxPerDay}` : '',
            ].filter(Boolean);
            const sosNote = sosParts.length > 0 ? `SOS — ${sosParts.join(', ')}` : 'SOS';
            comments = comments ? `${comments}; ${sosNote}` : sosNote;
          }

          if (!name) return null;
          return {
            name: unit ? `${name} ${unit}` : name,
            dosage,
            frequency,
            route,
            duration,
            relationWithMeal,
            comments,
            itemCode,
            medicineType,
            composition,
            timing,
            originalIndex: itemIndex,
          };
        }
        return null;
      })
      .filter((m): m is NonNullable<typeof m> => m !== null);
  };

  for (const field of order) {
    const fieldAliases = summaryFieldAliases[field] || [field];
    if (fieldAliases.some((key) => ignoredSummaryKeys.has(key))) {
      fieldAliases.forEach((key) => consumedKeys.add(key));
      continue;
    }

    // Ayurveda-only: skip the generic Treatment/Medications table entirely —
    // it's built from a legacy AI medication key-shape that doesn't match
    // the Ayurveda CDSS data, so it renders mostly blank. The correctly-
    // mapped Ayurveda medicines table already has its own dedicated card
    // elsewhere on this page, same convention as CaseSheetView.tsx's
    // AYURVEDA_HIDDEN_SECTIONS hiding 'treatmentPlan' for the same reason.
    // Diet/lifestyle are hidden here too since they're now shown nested
    // under Treatment Given inside that same dedicated Treatment card.
    // assessmentDiagnosis (alias 'diagnosis') is hidden too — its content is
    // the same Ayurvedic/Allopathic split already shown in the dedicated
    // Diagnosis card, so rendering it generically here just duplicates it.
    // Social History, Review of Systems, Allergies, Medication History, and
    // Doctor's Notes are hidden for Ayurveda too — they're not meaningfully
    // captured for these visits and only ever render an empty placeholder.
    if (
      isAyurveda &&
      (field === 'treatmentPlan' ||
        field === 'medications' ||
        field === 'prescribedMedications' ||
        field === 'diet' ||
        field === 'lifestyle' ||
        field === 'assessmentDiagnosis' ||
        field === 'socialHistory' ||
        field === 'reviewOfSystems' ||
        field === 'allergies' ||
        field === 'medicationHistory' ||
        field === 'doctorNotes')
    ) {
      fieldAliases.forEach((key) => consumedKeys.add(key));
      continue;
    }

    const group = departmentConfig?.fieldGroups?.[field];
    if (group) {
      // Check if this group contains medications
      const hasMedications = group.some(
        (child) => child === 'medications' || child === 'prescribedMedications'
      );

      if (hasMedications) {
        // Extract structured medications
        const medField = group.find(
          (child) => child === 'medications' || child === 'prescribedMedications'
        );
        const medications = medField ? extractMedications(medField) : [];

        // Build text content for other fields in the group, tracking each child's raw
        // key alongside its formatted text so edits can be written back individually.
        // Every child always appears (falling back to a "no data" message when empty),
        // except fields in fieldsHiddenWhenEmpty, which are dropped entirely when empty.
        const otherChildrenFields = group
          .filter((child) => child !== 'medications' && child !== 'prescribedMedications')
          .map((child) => {
            const label = summaryFieldLabels[child] || toSummaryLabel(child);
            const rawContent = consumeAsBulletHtml(child);
            if (!rawContent && fieldsHiddenWhenEmpty.has(child)) return null;
            return {
              label,
              content: rawContent || getSummaryEmptyMessage(child, label),
              sourceKey: resolveKey(child),
            };
          })
          .filter((f): f is { label: string; content: string; sourceKey: string } => f !== null);

        // Each child's label renders as its own bold heading (via <strong>,
        // which the surrounding [&_strong]:font-semibold class styles) above
        // its content, so a group like "Other History" visually reads as
        // nested sub-sections instead of a flat "Label: content" line.
        const otherChildrenText = otherChildrenFields.map(
          (f) => `<p><strong>${f.label}:</strong></p>${f.content}`
        );
        if (medications.length === 0) {
          otherChildrenText.unshift(
            `${summaryFieldLabels[medField || 'medications'] || 'Medication'}: ${getSummaryEmptyMessage(medField || 'medications', 'medication')}`
          );
        }

        sections.push({
          label:
            field === 'treatmentPlan'
              ? 'Treatment'
              : field === 'adviceFollowUp'
                ? 'Advice & Follow Up'
                : summaryFieldLabels[field] || toSummaryLabel(field),
          content: otherChildrenText.join('\n\n'),
          medications: medications.length > 0 ? medications : undefined,
          medicationsSourceKey: medField ? resolveKey(medField) : undefined,
          fields: otherChildrenFields.length > 0 ? otherChildrenFields : undefined,
        });
      } else {
        // No medications in this group - render as before, tracking each child's raw key.
        // Every child always appears (falling back to a "no data" message when empty),
        // except fields in fieldsHiddenWhenEmpty, which are dropped entirely when empty.
        const childrenFields = group
          .map((child) => {
            const label = summaryFieldLabels[child] || toSummaryLabel(child);
            const rawContent = consumeAsBulletHtml(child);
            if (!rawContent && fieldsHiddenWhenEmpty.has(child)) return null;
            return {
              label,
              content: rawContent || getSummaryEmptyMessage(child, label),
              sourceKey: resolveKey(child),
            };
          })
          .filter((f): f is { label: string; content: string; sourceKey: string } => f !== null);

        // Same bold-heading-per-child treatment as the medications-group
        // branch above, so every grouped section (Other History, Advice &
        // Follow Up, etc.) looks consistently nested.
        const childrenText = childrenFields.map(
          (f) => `<p><strong>${f.label}:</strong></p>${f.content}`
        );
        if (childrenFields.length > 0) {
          sections.push({
            label:
              field === 'treatmentPlan'
                ? 'Treatment'
                : field === 'adviceFollowUp'
                  ? 'Advice & Follow Up'
                  : summaryFieldLabels[field] || toSummaryLabel(field),
            content: childrenText.join('\n\n'),
            fields: childrenFields,
          });
        }
      }
      // Mark the group field itself consumed so the fallback loop won't re-render it.
      const groupFieldAliases = summaryFieldAliases[field] || [field];
      groupFieldAliases.forEach((key) => consumedKeys.add(key));
      continue;
    }

    const aliases = summaryFieldAliases[field] || [field];
    if (aliases.some((key) => consumedKeys.has(key))) {
      continue;
    }

    // Skip medications/prescribedMedications if they're part of a group
    // (they'll be rendered as part of the group, not as standalone)
    if (
      (field === 'medications' || field === 'prescribedMedications') &&
      departmentConfig?.fieldGroups &&
      Object.values(departmentConfig.fieldGroups).some(
        (group) => group.includes('medications') || group.includes('prescribedMedications')
      )
    ) {
      // Mark as consumed so the fallback loop doesn't render it
      aliases.forEach((key) => consumedKeys.add(key));
      continue;
    }

    // Group children are rendered under their parent, not again as standalone sections.
    if (
      departmentConfig?.fieldGroups &&
      Object.values(departmentConfig.fieldGroups).flat().includes(field)
    ) {
      continue;
    }

    // Handle standalone medications/prescribedMedications as a table — always shown,
    // with a "no data" message in place of the table when there are none.
    if (field === 'medications' || field === 'prescribedMedications') {
      const medications = extractMedications(field);
      const label = summaryFieldLabels[field] || toSummaryLabel(field);
      sections.push({
        label,
        content: medications.length === 0 ? getSummaryEmptyMessage(field, label) : '',
        medications: medications.length > 0 ? medications : undefined,
        medicationsSourceKey: resolveKey(field),
      });
      aliases.forEach((key) => consumedKeys.add(key));
      continue;
    }

    const label = summaryFieldLabels[field] || toSummaryLabel(field);
    // Renders directly from the raw per-field value: an array (the modern backend's
    // native shape for nearly every field) becomes one bullet per array element; a
    // legacy pre-joined string still goes through the old field-specific splitters.
    const bulletContent = consumeAsBulletHtml(field);
    const content =
      bulletContent ||
      (fieldsHiddenWhenEmpty.has(field) ? '' : getSummaryEmptyMessage(field, label));
    if (content) {
      sections.push({
        label,
        content,
        fields: [{ label, content, sourceKey: resolveKey(field) }],
      });
    }
  }

  // ICD-11 diagnosis codes table. The clinical symptom text itself is already shown via
  // the clinicalSymptoms field above (consumed from the same history_of_present_illness
  // object), so only its nested symptom_codes need pulling out into their own section.
  // Only shown when there are actual codes — unlike the fields in the loop above, this
  // isn't a configurable fieldOrder entry, so there's no "hidden when empty" placeholder
  // to fall back to; an empty table would just be noise.
  // Ayurveda-only: skip this generic table entirely — the dedicated
  // Ayurvedic/Allopathic Diagnosis card (right column) replaces it.
  if (!isAyurveda) {
    const hpiRaw = source['history_of_present_illness'];
    const symptomCodesRaw =
      hpiRaw && typeof hpiRaw === 'object' && !Array.isArray(hpiRaw)
        ? (hpiRaw as Record<string, unknown>).symptom_codes
        : undefined;
    if (Array.isArray(symptomCodesRaw) && symptomCodesRaw.length > 0) {
      const diagnosisCodes = symptomCodesRaw
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const obj = item as Record<string, unknown>;
          const symptom = typeof obj.symptom === 'string' ? obj.symptom : '';
          const symptomId = typeof obj.symptom_id === 'string' ? obj.symptom_id : '';
          const parts = symptomId.split('|');
          // "SYSTEM|CODE|DESCRIPTION" (e.g. "ICD-10|A78|Q fever") when the system is known;
          // falls back to treating the whole string as the code for older/unqualified data.
          const hasSystem = parts.length > 1;
          const type = hasSystem ? parts[0] : 'ICD-11';
          const code = hasSystem ? parts[1] : symptomId;
          const description = (hasSystem ? parts.slice(2).join('|') : '') || '';
          return symptom ? { symptom, code, description, type } : null;
        })
        .filter(
          (item): item is { symptom: string; code: string; description: string; type: string } =>
            item !== null
        );
      if (diagnosisCodes.length > 0) {
        sections.push({
          label: 'Diagnosis',
          content: '',
          diagnosisCodes,
        });
      }
    }
  }

  // Preserve fields introduced by the backend/config after this UI was shipped.
  for (const [key, value] of Object.entries(source)) {
    if (
      consumedKeys.has(key) ||
      ignoredSummaryKeys.has(key) ||
      ['timestamp', 'explanation', 'subject', 'encounter'].includes(key)
    ) {
      continue;
    }
    const bulletContent = summaryBulletsToHtmlList(value);
    const flattened = formatSummaryValue(value, key);
    const content = bulletContent || proseTextToHtmlList(flattened) || flattened;
    if (content) {
      const label = summaryFieldLabels[key] || toSummaryLabel(key);
      sections.push({ label, content, fields: [{ label, content, sourceKey: key }] });
    }
  }

  return sections.length > 0 ? sections : null;
}

export type Gender = 'M' | 'F' | 'O' | 'U';
export type StatusVariant = 'success' | 'warning' | 'destructive' | 'info' | 'default';

export interface PatientRecordViewProps {
  /** @deprecated No longer rendered — kept for backward-compat with call sites. */
  header?: {
    name?: string;
    identifier?: string;
    statusLabel?: string;
    statusVariant?: StatusVariant;
  };
  patient: {
    name: string;
    dateOfBirth: string;
    age: number;
    weight?: number | null;
    gender: Gender;
    maritalStatus?: 'S' | 'M' | 'D' | 'W' | 'U' | null;
    identifier: string;
    createdAt: string;
    phoneNumber?: string | null;
    email?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    emergencyContactName?: string | null;
    emergencyContactPhone?: string | null;
    visitSummary?: string | null;
    visitSummaryLastUpdated?: string | null;
  };
  actions: {
    onBack: () => void;
    onEdit?: () => void;
    /** Persist an edited visit's conclusion JSON back to its DiagnosticReport. */
    onSaveVisitEdit?: (reportId: string, conclusionJson: string) => Promise<void>;
    onDelete: () => void;
    onStartConsultation?: () => void;
    onCopy?: () => void;
    onDownload?: () => void;
    onRefreshSummary?: () => void;
    /** Persist edited vitals as Observations for the given encounter. */
    onSaveVitals?: (encounterId: string, vitals: VitalSigns) => Promise<void>;
  };
  isRefreshingSummary?: boolean;
  deleteConfirm: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
    pending?: boolean;
  };
  loading?: boolean;
  error?: string | null;
  /** Patient FHIR ID — used for lab report upload/fetch */
  patientId?: string;
  /** Whether the current doctor is an Ayurveda practitioner */
  isAyurveda?: boolean;
  /** Tab control for role-based visit filtering */
  tabs?: {
    activeTab: VisitTab;
    onTabChange: (tab: VisitTab) => void;
    myVisitsLabel: string;
    otherVisitsLabel: string;
    otherVisitsKey: VisitTab;
  };
  /** Practitioner filter for administrators */
  practitionerFilter?: {
    value: string | undefined;
    onChange: (value: string | undefined) => void;
    options: Array<{ id: string; name: string }>;
  };
  /** Department configuration for dynamic case sheet rendering */
  departmentConfig?: {
    fieldOrder: string[];
    fieldGroups?: Record<string, string[]>;
  } | null;
  /** Department key - if present, use structured summary format */
  departmentKey?: string | null;
  reports: {
    items: Array<{
      id: string;
      title: string;
      status: string;
      issued?: string;
      encounter?: string | null;
      source?: 'DocumentSource' | 'composition' | 'diagnosticReport';
      practitionerName?: string;
    }>;
    isLoading: boolean;
    error?: string | null;
    selectedId?: string | null;
    onSelect: (reportId: string) => void;
    detail: {
      item?: {
        id: string;
        title: string;
        status: string;
        issued?: string | null;
        effective?: string | null;
        encounter?: string | null;
        subject?: string | null;
        conclusion?: string | null;
        source?: 'composition' | 'diagnosticReport' | 'DocumentSource';
        soapData?: Record<string, string | null | unknown[] | undefined>;
        documentSourceData?: unknown;
        practitionerName?: string;
      };
      isLoading: boolean;
      error?: string | null;
    };
  };
  /** Vitals for the currently selected (or most recent) visit */
  vitals?: VitalSigns;
  isLoadingVitals?: boolean;
  /** Encounter the displayed vitals belong to — required to save an edit back to Medplum. */
  vitalsEncounterId?: string | null;
}

export const PatientRecordView: React.FC<PatientRecordViewProps> = ({
  patient,
  actions,
  deleteConfirm,
  loading,
  error,
  isAyurveda,
  tabs,
  practitionerFilter,
  departmentConfig,
  departmentKey,
  reports,
  isRefreshingSummary,
  patientId,
  vitals,
  isLoadingVitals,
  vitalsEncounterId,
}) => {
  const loggedInUser = useAuthStore((state) => state.user);
  const projectConfig = useAuthStore((state) => state.projectConfig);
  const prescriptionPageSize =
    projectConfig?.otherConfig?.prescriptionConfig?.prescriptionSize?.size === 'A5' ? 'A5' : 'A4';

  const deleteDisabled =
    typeof actions?.onDelete !== 'function' || typeof deleteConfirm?.onConfirm !== 'function';

  // Process consultation history from reports
  const consultationHistory = React.useMemo(() => {
    if (!reports.items || reports.items.length === 0) return [];

    // Sort by date descending (newest first)
    const sortedReports = [...reports.items].sort((a, b) => {
      const dateA = a.issued ? new Date(a.issued).getTime() : 0;
      const dateB = b.issued ? new Date(b.issued).getTime() : 0;
      return dateB - dateA; // Newest first
    });

    return sortedReports.map((report, index, sortedReports) => ({
      id: report.id,
      visitNumber: sortedReports.length - index,
      title: report.title || 'Consultation',
      date: report.issued || '',
      timeElapsed: report.issued ? getTimeElapsed(report.issued) : '',
      isSelected: reports.selectedId === report.id,
      practitionerName: report.practitionerName,
    }));
  }, [reports.items, reports.selectedId]);

  // Get selected report for summary
  const selectedReport = reports.detail.item;

  // If departmentKey is set, use structured summary format (e.g., Bhakti Vedanta)
  // If departmentKey is null, use AI paragraph format (e.g., Pranik/Swecha)
  const showStructuredSummary = !!departmentKey;

  // Find selected visit from consultation history
  const selectedVisit = React.useMemo(() => {
    return consultationHistory.find((visit) => visit.isSelected);
  }, [consultationHistory]);

  // Parse the selected report's conclusion as JSON
  const parsedReport = React.useMemo<Record<string, unknown> | null>(() => {
    if (!selectedReport?.conclusion) return null;
    try {
      return JSON.parse(selectedReport.conclusion) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [selectedReport?.conclusion]);

  // Allergy conflict alerts (e.g. a prescribed medication clashing with a
  // stated allergy) — read-only on this page, same CDSS data CaseSheetView
  // surfaces as editable cards, shown here purely for visibility.
  const allergyConflictAlerts = React.useMemo<AllergyConflictAlert[]>(() => {
    if (!parsedReport) return [];
    const unwrapped = unwrapSummarySource(parsedReport);
    const pranikCdss = (unwrapped.pranik_cdss ?? unwrapped.pranikCdss) as PranikCDSS | undefined;
    const value = pranikCdss?.allergyConflictAlerts?.value;
    return Array.isArray(value) ? value : [];
  }, [parsedReport]);

  // Read-only allergy-alert cards shown under the summary, same data/card
  // component Case Sheet uses — no add/replace actions here since Patient
  // Record isn't an active prescribing surface.
  const allergyAlertsSection =
    allergyConflictAlerts.length > 0 ? (
      <div className="mt-4 space-y-4">
        <p className="font-semibold text-[#061A61]">Allergy Conflict Alerts:</p>
        <div className="space-y-2">
          {allergyConflictAlerts.map((alert, idx) => (
            <CDSSMedicationCard
              key={`patient-record-allergy-alert-${idx}`}
              cardType="allergy"
              suggestion={{
                medication: alert.medication,
                medication_id: alert.medication_id,
                explanation: alert.explanation,
                replace_with: alert.replace_with,
                replace_with_medication_id: alert.replace_with_medication_id,
                source_url: alert.source_url,
                allergy: alert.allergy,
                allergy_timestamp: alert.allergy_timestamp,
                conflicting_medication: alert.conflicting_medication,
                conflicting_medication_timestamp: alert.conflicting_medication_timestamp,
                severity: alert.severity,
                evidence_confidence: alert.evidence_confidence,
                source_phrase: alert.source_phrase,
              }}
            />
          ))}
        </div>
      </div>
    ) : null;

  // Derive the structured sections to render in the centre column.
  //
  // Composition-sourced (AI SOAP note) visits have free-form section titles the AI
  // Physician Assist invents per visit — they don't map onto the case-sheet field
  // schema `buildSummarySections`/`departmentConfig` expect. Render them directly,
  // one block per section, in original order, instead of running them through the
  // case-sheet field-matching pipeline (which double-matched sections against
  // overlapping field aliases and separately dumped the raw array a second time).
  //
  // DiagnosticReport-sourced (case-sheet/CDSS) visits keep using buildSummarySections,
  // which handles every known case-sheet field shape and falls back gracefully for
  // unknown future backend fields.
  const formattedSummary = React.useMemo(() => {
    if (selectedReport?.source === 'composition') {
      return mapCompositionSections(selectedReport.soapData?.sections);
    }
    return buildSummarySections(
      (parsedReport || null) as Record<string, unknown> | null,
      departmentConfig,
      isAyurveda
    );
  }, [
    selectedReport?.source,
    selectedReport?.soapData,
    parsedReport,
    departmentConfig,
    isAyurveda,
  ]);

  // Inline visit editing — offered for every visit source (DiagnosticReport,
  // DocumentSource, Composition); the page routes the save to the right resource.
  // Editing happens right in this panel: text fields and the medications table are
  // tagged with the raw JSON key they came from (see `fields`/`medicationsSourceKey`
  // on SummarySection), so saving only overwrites those exact keys and leaves every
  // other field untouched. Composition sections are keyed by their title.
  const isEditableVisit = !!selectedVisit && !!selectedReport?.source;
  // DocumentSource fields stored as lists of objects (family history, physical
  // examination, diagnoses…) can't be rebuilt faithfully from a text box, so they
  // stay read-only in the editor instead of accepting edits that would be dropped.
  const readOnlyStructuredKeys = React.useMemo(() => {
    const keys = new Set<string>();
    if (selectedReport?.source !== 'DocumentSource' || !parsedReport) return keys;
    Object.entries(parsedReport).forEach(([key, field]) => {
      const value =
        field && typeof field === 'object' && !Array.isArray(field)
          ? (field as { value?: unknown }).value
          : field;
      if (Array.isArray(value) && value.some((item) => item && typeof item === 'object')) {
        keys.add(key);
      }
    });
    return keys;
  }, [selectedReport?.source, parsedReport]);
  // Investigations live in their own structured field, which Composition visits
  // don't have (there it is just another section), so its pencil is hidden there.
  const isInvestigationsEditable = isEditableVisit && selectedReport?.source !== 'composition';
  const [isEditingVisit, setIsEditingVisit] = React.useState(false);
  const [editedFields, setEditedFields] = React.useState<Record<string, string>>({});
  const [editedMedications, setEditedMedications] = React.useState<
    Record<string, MedicationEntry[]>
  >({});
  const [isSavingVisit, setIsSavingVisit] = React.useState(false);

  // Leaving edit mode whenever the selected visit changes avoids carrying stale
  // draft edits over to a different visit.
  React.useEffect(() => {
    setIsEditingVisit(false);
  }, [selectedReport?.id]);

  const handleStartEditVisit = React.useCallback(() => {
    const fields: Record<string, string> = {};
    const meds: Record<string, MedicationEntry[]> = {};
    (formattedSummary || []).forEach((section) => {
      section.fields?.forEach((field) => {
        if (readOnlyStructuredKeys.has(field.sourceKey)) return;
        // Field content may carry markup (e.g. "<b>Itchy rash</b>") meant for the
        // read-only dangerouslySetInnerHTML render. Represent bold/italic as
        // **/* markdown markers in the textarea instead of dropping them, so
        // formatting survives an edit instead of being silently stripped.
        fields[field.sourceKey] = htmlToEditableText(field.content);
      });
      if (section.medicationsSourceKey) {
        meds[section.medicationsSourceKey] = section.medications ? [...section.medications] : [];
      }
    });
    setEditedFields(fields);
    setEditedMedications(meds);
    setIsEditingVisit(true);
  }, [formattedSummary, readOnlyStructuredKeys]);

  const handleCancelEditVisit = React.useCallback(() => {
    setIsEditingVisit(false);
  }, []);

  const handleSaveVisitEdit = React.useCallback(async () => {
    if (!selectedReport?.id || !actions.onSaveVisitEdit) return;

    const merged: Record<string, unknown> = { ...(parsedReport || {}) };
    Object.entries(editedFields).forEach(([key, value]) => {
      const original = merged[key];
      const htmlValue = editableTextToHtml(value);
      // Some fields (e.g. history_of_present_illness) are stored as an object like
      // { value: "...", symptom_codes: [...] } — the ICD-11 Diagnosis table is read
      // straight out of that sibling `symptom_codes` data. Overwriting the whole key
      // with the edited plain text would silently delete it, so merge into `.value`
      // and keep every other property on the object intact.
      if (original && typeof original === 'object' && !Array.isArray(original)) {
        merged[key] = { ...(original as Record<string, unknown>), value: htmlValue };
      } else {
        merged[key] = htmlValue;
      }
    });
    Object.entries(editedMedications).forEach(([key, rows]) => {
      merged[key] = rows
        .filter((row) => row.name.trim())
        .map((row) => ({
          medication: row.name,
          dosage: row.dosage,
          frequency: row.frequency,
          route: row.route,
          duration: row.duration,
          relation_with_meal: row.relationWithMeal,
          additional_info: row.comments,
          item_code: row.itemCode,
          medicine_type: row.medicineType,
          composition: row.composition,
          // Lets the page merge this row over the saved entry (see applyVisitEditToSummary);
          // stripped again for visits saved as a plain conclusion.
          original_index: row.originalIndex,
          // Backend schema expects an array (e.g. ["Morning", "Night"]) —
          // row.timing is the flattened "Morning, Night" display string, so
          // split it back apart rather than saving a single joined string
          // where the backend expects a list.
          timing: row.timing
            ? row.timing
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean)
            : [],
        }));
    });

    setIsSavingVisit(true);
    try {
      await actions.onSaveVisitEdit(selectedReport.id, JSON.stringify(merged));
      setIsEditingVisit(false);
    } catch {
      // Already surfaced via toast by the mutation; keep the editor open so the
      // user's edits aren't lost and they can retry.
    } finally {
      setIsSavingVisit(false);
    }
  }, [selectedReport?.id, actions, parsedReport, editedFields, editedMedications]);

  const updateMedicationRow = (
    sourceKey: string,
    index: number,
    field: keyof MedicationEntry,
    value: string
  ) => {
    setEditedMedications((prev) => ({
      ...prev,
      [sourceKey]: (prev[sourceKey] || []).map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const addMedicationRow = (sourceKey: string) => {
    setEditedMedications((prev) => ({
      ...prev,
      [sourceKey]: [
        ...(prev[sourceKey] || []),
        {
          name: '',
          dosage: '',
          frequency: '',
          route: '',
          duration: '',
          relationWithMeal: '',
          comments: '',
          itemCode: '',
          medicineType: '',
          composition: '',
          timing: '',
        },
      ],
    }));
  };

  const removeMedicationRow = (sourceKey: string, index: number) => {
    setEditedMedications((prev) => ({
      ...prev,
      [sourceKey]: (prev[sourceKey] || []).filter((_, i) => i !== index),
    }));
  };

  // Vitals editing — a separate small resource (FHIR Observations, not the
  // DiagnosticReport conclusion JSON), saved through its own mutation, so it gets
  // its own edit toggle independent of the visit-note editor above.
  const [isEditingVitals, setIsEditingVitals] = React.useState(false);
  const [editedVitals, setEditedVitals] = React.useState<VitalSigns>({});
  const [isSavingVitals, setIsSavingVitals] = React.useState(false);

  React.useEffect(() => {
    setIsEditingVitals(false);
  }, [vitalsEncounterId]);

  const handleStartEditVitals = React.useCallback(() => {
    setEditedVitals(vitals || {});
    setIsEditingVitals(true);
  }, [vitals]);

  const handleCancelEditVitals = React.useCallback(() => {
    setIsEditingVitals(false);
  }, []);

  const handleSaveVitals = React.useCallback(async () => {
    if (!vitalsEncounterId || !actions.onSaveVitals) return;
    setIsSavingVitals(true);
    try {
      await actions.onSaveVitals(vitalsEncounterId, editedVitals);
      setIsEditingVitals(false);
    } catch {
      // Already surfaced via toast by the mutation; keep the editor open.
    } finally {
      setIsSavingVitals(false);
    }
  }, [vitalsEncounterId, actions, editedVitals]);

  // Astha Sthaana Pareeksha (Prakriti/Vikriti/Agni/Ama + Nadi/Mootram/etc.),
  // for the dedicated right-column card (Ayurveda only). Saved as an HTML
  // bullet-list string (same `doshaProfile` field CaseSheetPage builds and
  // CaseSheetView labels "Astha Sthaana Pareeksha") — rendered as HTML here
  // too, via doshaProfileHtml below, instead of as escaped plain text.
  const doshaProfileHtml = React.useMemo(() => {
    const dp = parsedReport?.dosha_profile;
    return typeof dp === 'string' && dp.trim() ? dp : '';
  }, [parsedReport]);

  const doshaProfile = React.useMemo(() => {
    const dp = parsedReport?.dosha_profile;
    if (!dp || typeof dp === 'string') return [];
    if (typeof dp === 'object') {
      const d = dp as Record<string, unknown>;
      return [
        d.prakriti ? `Prakriti: ${d.prakriti}` : '',
        d.vikriti ? `Vikriti: ${d.vikriti}` : '',
        d.manasik_guna ? `Manasik Guna: ${d.manasik_guna}` : '',
      ].filter(Boolean);
    }
    return [];
  }, [parsedReport]);

  // Ayurveda-only Personal History (Diet/Appetite/Sleep/Exercise/Bowel/
  // Bladder/Addiction/Menstrual), for the dedicated right-column card —
  // same convention as doshaProfile above.
  const ayurvedaPersonalHistory = React.useMemo(() => {
    const ph = parsedReport?.personal_history;
    if (!ph || typeof ph !== 'object') return [];
    const obj = ph as {
      diet?: string | null;
      appetite?: string | null;
      sleep?: string | null;
      exercise?: string | null;
      bowel?: string | null;
      bladder?: string | null;
      addiction?: string | null;
      menstrual?: string | null;
    };
    const fieldMap: Array<[keyof typeof obj, string]> = [
      ['diet', 'Diet'],
      ['appetite', 'Appetite'],
      ['sleep', 'Sleep'],
      ['exercise', 'Exercise'],
      ['bowel', 'Bowel'],
      ['bladder', 'Bladder'],
      ['addiction', 'Addiction'],
      ['menstrual', 'Menstrual'],
    ];
    return fieldMap.filter(([key]) => obj[key]).map(([key, label]) => `${label}: ${obj[key]}`);
  }, [parsedReport]);

  // Ayurveda-only Diagnosis (Ayurvedic/Allopathic), for the dedicated
  // right-column card — same convention as doshaProfile above.
  const ayurvedaDiagnosis = React.useMemo(() => {
    const d = parsedReport?.diagnosis;
    if (!d || typeof d !== 'object') return [];
    const obj = d as { ayurvedic?: string | null; allopathic?: string | null };
    return [
      obj.ayurvedic ? `Ayurvedic: ${obj.ayurvedic}` : '',
      obj.allopathic ? `Allopathic: ${obj.allopathic}` : '',
    ].filter(Boolean);
  }, [parsedReport]);

  // Ayurveda-only Treatment (Medicines/Treatment Principle/Treatment Given),
  // for the dedicated right-column card — same convention as doshaProfile above.
  interface AyurvedaPrescribedMedicine {
    name?: string | null;
    form?: string | null;
    dose?: string | null;
    anupana?: string | null;
    timing?: string | null;
    frequency?: string | null;
    duration?: string | null;
    additional_info?: string | null;
  }
  const ayurvedaTreatment = React.useMemo(() => {
    const unwrapItems = (field: unknown): string[] => {
      if (!field) return [];
      if (Array.isArray(field)) return field.filter((v): v is string => typeof v === 'string');
      if (typeof field === 'object' && field !== null && 'value' in field) {
        const val = (field as { value: unknown }).value;
        return Array.isArray(val) ? val.filter((v): v is string => typeof v === 'string') : [];
      }
      return [];
    };
    // Treatment Given has no text of its own — same as the Case Sheet, it's
    // just a heading directly containing its two nested sub-lists, Diet and
    // Lifestyle (same pattern as Diagnosis containing Ayurvedic/Allopathic).
    const dietItems = unwrapItems(parsedReport?.diet_recommendations);
    const lifestyleItems = unwrapItems(parsedReport?.lifestyle_recommendations);

    const t = parsedReport?.treatment;
    if (!t || typeof t !== 'object') {
      return {
        medicinesText: '',
        medicinesTable: [] as AyurvedaPrescribedMedicine[],
        lines: [] as string[],
        dietItems,
        lifestyleItems,
      };
    }
    const obj = t as {
      medicines?: string | string[] | null;
      treatmentPrinciple?: string | null;
      prescribedMedicines?: AyurvedaPrescribedMedicine[] | null;
    };
    const medicinesTable = Array.isArray(obj.prescribedMedicines) ? obj.prescribedMedicines : [];
    const medicinesText = Array.isArray(obj.medicines)
      ? obj.medicines.join(', ')
      : obj.medicines || '';
    const lines = [
      obj.treatmentPrinciple ? `Treatment Principle: ${obj.treatmentPrinciple}` : '',
    ].filter(Boolean);
    return { medicinesText, medicinesTable, lines, dietItems, lifestyleItems };
  }, [parsedReport]);

  // Right-column medication list — structured objects for table rendering.
  interface MedicationRow {
    name: string;
    dosage: string;
    frequency: string;
    route: string;
    duration: string;
    relationWithMeal: string;
    comments: string;
    itemCode: string;
    medicineType: string;
  }
  const medications = React.useMemo(() => {
    const raw = parsedReport?.medications;
    if (!raw) return [];
    const list =
      typeof raw === 'object' && raw !== null && 'value' in (raw as object)
        ? (raw as { value: unknown }).value
        : raw;
    const items = Array.isArray(list) ? list : [list];
    return items
      .map((item: unknown): MedicationRow | null => {
        if (!item) return null;
        if (typeof item === 'string') {
          const trimmed = item.trim();
          if (!trimmed) return null;
          return {
            name: trimmed,
            dosage: '',
            frequency: '',
            route: '',
            duration: '',
            relationWithMeal: '',
            comments: '',
            itemCode: '',
            medicineType: '',
          };
        }
        if (typeof item === 'object') {
          const obj = item as Record<string, unknown>;
          const name = formatSummaryValue(obj.medication ?? obj.name ?? obj.drug_name) || '';
          const dosage = formatSummaryValue(obj.dosage ?? obj.dose ?? obj.strength) || '';
          const unit = formatSummaryValue(obj.unit) || '';
          const frequency = formatSummaryValue(obj.frequency ?? obj.period ?? obj.schedule) || '';
          const route = formatSummaryValue(obj.route ?? obj.intake_type) || '';
          const duration = formatSummaryValue(obj.duration ?? obj.days) || '';
          const relationWithMeal =
            formatSummaryValue(obj.relation_with_meal ?? obj.relationWithMeal) || '';
          const comments =
            formatSummaryValue(obj.additional_info ?? obj.additionalInfo ?? obj.comments) || '';
          const itemCode = formatSummaryValue(obj.item_code ?? obj.itemCode) || '';
          const medicineType = formatSummaryValue(obj.medicine_type ?? obj.medicineType) || '';
          if (!name) return null;
          return {
            name: unit ? `${name} ${unit}` : name,
            dosage,
            frequency,
            route,
            duration,
            relationWithMeal,
            comments,
            itemCode,
            medicineType,
          };
        }
        return null;
      })
      .filter((m): m is MedicationRow => m !== null);
  }, [parsedReport]);

  // Right-column investigations list. Reads the raw array's own items
  // directly (never flattening to a comma-joined string and re-splitting —
  // that's ambiguous whenever an item contains its own comma, same class of
  // bug fixed elsewhere for Medical History/Patient Summary) with a
  // sentence/list-comma-splitting fallback for a legacy plain-string value.
  const investigations = React.useMemo(() => {
    const raw = parsedReport?.investigations_orders;
    if (!raw) return [];
    const unwrapped =
      raw && typeof raw === 'object' && !Array.isArray(raw) && 'value' in (raw as object)
        ? (raw as { value?: unknown }).value
        : raw;
    if (Array.isArray(unwrapped)) {
      return unwrapped
        .map((item) => (typeof item === 'string' ? item.trim() : formatSummaryValue(item)))
        .filter(Boolean);
    }
    return splitProseToBullets(formatSummaryValue(raw, 'investigations'));
  }, [parsedReport]);

  // The raw JSON key investigations actually live under — same alias resolution the
  // visit-note editor uses, so editing here writes back to the exact same field.
  const investigationsSourceKey = React.useMemo(() => {
    const aliases = summaryFieldAliases.investigations || ['investigations_orders'];
    const found = aliases.find((key) => parsedReport?.[key]);
    return found || aliases[0];
  }, [parsedReport]);

  // Investigations editing is intentionally independent of the visit-note editor
  // (isEditingVisit) below — clicking its pencil should only affect this card, not
  // switch Chief Complaint/Treatment/Summary into edit mode too. It still writes to
  // the same DiagnosticReport conclusion JSON, touching only the investigations key.
  const [isEditingInvestigations, setIsEditingInvestigations] = React.useState(false);
  const [editedInvestigations, setEditedInvestigations] = React.useState('');
  const [isSavingInvestigations, setIsSavingInvestigations] = React.useState(false);

  React.useEffect(() => {
    setIsEditingInvestigations(false);
  }, [selectedReport?.id]);

  const handleStartEditInvestigations = React.useCallback(() => {
    setEditedInvestigations(investigations.join('\n'));
    setIsEditingInvestigations(true);
  }, [investigations]);

  const handleCancelEditInvestigations = React.useCallback(() => {
    setIsEditingInvestigations(false);
  }, []);

  const handleSaveInvestigations = React.useCallback(async () => {
    if (!selectedReport?.id || !actions.onSaveVisitEdit) return;
    const merged: Record<string, unknown> = { ...(parsedReport || {}) };
    const original = merged[investigationsSourceKey];
    const joined = editedInvestigations
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)
      // DocumentSource keeps investigations as a list of items, so keep the one-per-line
      // boundaries (items may themselves contain commas); other visits store one string.
      .join(selectedReport.source === 'DocumentSource' ? '\n' : ', ');
    // Preserve any sibling data (e.g. a { value, ... } wrapper) the same way the
    // visit-note editor does, instead of blindly replacing the whole key.
    merged[investigationsSourceKey] =
      original && typeof original === 'object' && !Array.isArray(original)
        ? { ...(original as Record<string, unknown>), value: joined }
        : joined;

    setIsSavingInvestigations(true);
    try {
      await actions.onSaveVisitEdit(selectedReport.id, JSON.stringify(merged));
      setIsEditingInvestigations(false);
    } catch {
      // Already surfaced via toast by the mutation; keep the editor open.
    } finally {
      setIsSavingInvestigations(false);
    }
  }, [selectedReport?.id, actions, parsedReport, investigationsSourceKey, editedInvestigations]);

  // Format address
  const formattedAddress = React.useMemo(() => {
    const parts = [
      patient.address,
      patient.city,
      patient.state,
      patient.postalCode,
      patient.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Address not provided';
  }, [patient.address, patient.city, patient.state, patient.postalCode, patient.country]);

  // Format gender display
  const genderDisplay = React.useMemo(() => {
    switch (patient.gender) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'O':
        return 'Other';
      default:
        return 'Unknown';
    }
  }, [patient.gender]);

  // Format marital status display
  const maritalStatusDisplay = React.useMemo(() => {
    switch (patient.maritalStatus) {
      case 'S':
        return 'Single';
      case 'M':
        return 'Married';
      case 'D':
        return 'Divorced';
      case 'W':
        return 'Widowed';
      case 'U':
        return 'Unknown';
      default:
        return '-';
    }
  }, [patient.maritalStatus]);

  // Format date of birth
  const formattedDOB = React.useMemo(() => {
    if (!patient.dateOfBirth || patient.dateOfBirth === 'Invalid Date') {
      return 'N/A';
    }
    try {
      const date = new Date(patient.dateOfBirth);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return patient.dateOfBirth;
    }
  }, [patient.dateOfBirth]);

  // Prescription for the currently displayed visit (selected, or most-recent when none
  // is explicitly selected) — mirrors the case-sheet's Print Prescription button, built
  // from the same summary sections/medications/investigations already rendered here.
  const prescriptionData = React.useMemo<PrescriptionData | null>(() => {
    // DocumentSource visits carry the same case-sheet shape in `conclusion`, so they print too.
    if (
      !selectedReport ||
      (selectedReport.source !== 'diagnosticReport' &&
        selectedReport.source !== 'DocumentSource') ||
      !parsedReport
    )
      return null;

    const findSection = (label: string) => formattedSummary?.find((s) => s.label === label);

    const textOf = (label: string): string => {
      const content = findSection(label)?.content?.trim() || '';
      return Object.values(summaryEmptyMessages).includes(content) ||
        content === 'No diagnosis recorded'
        ? ''
        : content;
    };

    const rawMedications = parsedReport
      ? findSummaryValue(unwrapSummarySource(parsedReport), 'medications')
      : undefined;
    const unwrappedMedications =
      typeof rawMedications === 'object' && rawMedications !== null && 'value' in rawMedications
        ? (rawMedications as { value: unknown }).value
        : rawMedications;
    const medicationItems = rawMedications
      ? Array.isArray(unwrappedMedications)
        ? unwrappedMedications
        : [unwrappedMedications]
      : [];
    const structuredMedications = medicationItems
      .map((item) => toStructuredMedication(item))
      .filter((m): m is StructuredMedication => m !== null);

    return {
      pageSize: prescriptionPageSize,
      patient: {
        name: patient.name,
        phone: patient.phoneNumber || undefined,
        identifier: patient.identifier,
        age: patient.age,
        gender: genderDisplay,
        dateOfBirth: formattedDOB !== 'N/A' ? formattedDOB : undefined,
        address: formattedAddress || undefined,
      },
      date: selectedReport.issued
        ? new Date(selectedReport.issued).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '',
      doctorName:
        selectedReport.practitionerName ||
        (loggedInUser?.firstName && loggedInUser?.lastName
          ? `${loggedInUser.firstName} ${loggedInUser.lastName}`
          : loggedInUser?.username) ||
        undefined,
      vitals: vitals
        ? {
            height: vitals.height,
            weight: vitals.weight,
            temperature: vitals.temperature,
            bloodPressure: vitals.bloodPressure,
            heartRate: vitals.heartRate,
            respiratoryRate: vitals.respiratoryRate,
            oxygenSaturation: vitals.oxygenSaturation,
          }
        : undefined,
      complaints: textOf(summaryFieldLabels.chiefComplaint),
      // ICD-11 diagnosis codes are pushed as their own "Diagnosis" section (see
      // buildSummarySections), separate from the free-text "Assessment & Diagnosis"
      // field — the case sheet prefers the codes when present, falling back to the
      // free-text diagnosis otherwise.
      diagnosis: textOf(summaryFieldLabels.assessmentDiagnosis) || textOf('Diagnosis'),
      diagnosisCodes: findSection('Diagnosis')?.diagnosisCodes,
      medications: structuredMedications,
      // The Ayurveda Medicines table (treatment.prescribedMedicines) is the
      // fully-structured source; for Ayurveda visits the generic `medications`
      // list often holds only a legacy plain-text sentence per medicine, so the
      // generator prints the table exclusively when it has rows.
      ayurvedaMedicines:
        ayurvedaTreatment.medicinesTable.length > 0
          ? ayurvedaTreatment.medicinesTable
          : medicationItems
              .map((item) => toPrescribedMedicine(item))
              .filter((m): m is PrescribedMedicine => m !== null),
      investigations: investigations.length > 0 ? investigations : undefined,
      doctorNotes: textOf(summaryFieldLabels.doctorNotes) || undefined,
    };
  }, [
    selectedReport,
    formattedSummary,
    parsedReport,
    patient,
    genderDisplay,
    formattedDOB,
    formattedAddress,
    vitals,
    investigations,
    loggedInUser,
    ayurvedaTreatment,
    prescriptionPageSize,
  ]);

  const handlePrintPrescription = React.useCallback(() => {
    if (!prescriptionData) return;
    printPrescription(prescriptionData);
  }, [prescriptionData]);

  // Build full summary text including medications, investigations, dosha profile, and patient info
  const buildFullSummaryText = React.useCallback(() => {
    const lines: string[] = [];

    // Patient header
    lines.push(`Patient: ${patient.name}`);
    lines.push(`ID: ${patient.identifier}`);
    lines.push(`DOB: ${formattedDOB}`);
    lines.push(`Age/Gender: ${patient.age}/${genderDisplay}`);
    if (typeof patient.weight === 'number') {
      lines.push(`Weight: ${patient.weight} kg`);
    }
    lines.push(`Marital Status: ${maritalStatusDisplay}`);
    lines.push(`Address: ${formattedAddress}`);
    if (patient.phoneNumber) {
      lines.push(`Phone: ${patient.phoneNumber}`);
    }
    lines.push('');
    lines.push('='.repeat(60));
    lines.push('');

    if (selectedVisit) {
      // Visit header
      lines.push(`Visit ${selectedVisit.visitNumber}`);
      lines.push(`Date: ${selectedVisit.date}`);
      lines.push('');
      lines.push('-'.repeat(60));
      lines.push('');

      // Summary sections
      if (formattedSummary) {
        formattedSummary.forEach((section) => {
          lines.push(`${section.label}:`);
          // section.content is HTML (built by consumeAsBulletHtml) for any
          // field with real data — convert it to readable bulleted plain
          // text instead of either leaking raw <ul>/<li> tags or losing the
          // bullet markers entirely.
          lines.push(htmlSectionToPlainText(section.content));
          lines.push('');
        });
      }

      // Investigations
      if (investigations.length > 0) {
        lines.push('Investigations:');
        investigations.forEach((inv) => {
          lines.push(`  - ${inv}`);
        });
        lines.push('');
      }

      // Astha Sthaana Pareeksha (Ayurveda only)
      if (isAyurveda && doshaProfileHtml) {
        lines.push('Astha Sthaana Pareeksha:');
        stripHtml(doshaProfileHtml)
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .forEach((line) => lines.push(`  - ${line}`));
        lines.push('');
      } else if (isAyurveda && doshaProfile.length > 0) {
        lines.push('Astha Sthaana Pareeksha:');
        doshaProfile.forEach((item) => {
          lines.push(`  - ${item}`);
        });
        lines.push('');
      }

      // Personal History (Ayurveda only)
      if (isAyurveda && ayurvedaPersonalHistory.length > 0) {
        lines.push('Personal History:');
        ayurvedaPersonalHistory.forEach((item) => {
          lines.push(`  - ${item}`);
        });
        lines.push('');
      }

      // Diagnosis (Ayurveda only)
      if (isAyurveda && ayurvedaDiagnosis.length > 0) {
        lines.push('Diagnosis:');
        ayurvedaDiagnosis.forEach((item) => {
          lines.push(`  - ${item}`);
        });
        lines.push('');
      }

      // Treatment (Ayurveda only)
      if (
        isAyurveda &&
        (ayurvedaTreatment.medicinesTable.length > 0 ||
          ayurvedaTreatment.medicinesText ||
          ayurvedaTreatment.lines.length > 0 ||
          ayurvedaTreatment.dietItems.length > 0 ||
          ayurvedaTreatment.lifestyleItems.length > 0)
      ) {
        lines.push('Treatment:');
        if (ayurvedaTreatment.medicinesTable.length > 0) {
          lines.push('  Medicines:');
          ayurvedaTreatment.medicinesTable.forEach((med) => {
            const parts = [
              med.name,
              med.form,
              med.dose,
              med.frequency,
              med.duration,
              med.anupana,
              med.timing,
              med.additional_info,
            ].filter(Boolean);
            lines.push(`    - ${parts.join(', ')}`);
          });
        } else if (ayurvedaTreatment.medicinesText) {
          lines.push(`  Medicines: ${ayurvedaTreatment.medicinesText}`);
        }
        ayurvedaTreatment.lines.forEach((line) => {
          lines.push(`  ${line}`);
        });
        // Treatment Given has no text of its own — it's just a heading
        // directly containing its two nested sub-lists, Diet and Lifestyle.
        if (ayurvedaTreatment.dietItems.length > 0 || ayurvedaTreatment.lifestyleItems.length > 0) {
          lines.push('  Treatment Given:');
          if (ayurvedaTreatment.dietItems.length > 0) {
            lines.push('    Diet:');
            ayurvedaTreatment.dietItems.forEach((item) => lines.push(`      - ${item}`));
          }
          if (ayurvedaTreatment.lifestyleItems.length > 0) {
            lines.push('    Lifestyle:');
            ayurvedaTreatment.lifestyleItems.forEach((item) => lines.push(`      - ${item}`));
          }
        }
        lines.push('');
      }
    } else {
      // Overall summary
      if (patient.visitSummary) {
        lines.push('Summary of all visits:');
        lines.push('');
        lines.push(htmlSectionToPlainText(formatVisitSummaryText(patient.visitSummary)));
      }
    }

    return lines.join('\n');
  }, [
    patient,
    selectedVisit,
    formattedSummary,
    medications,
    investigations,
    doshaProfile,
    doshaProfileHtml,
    ayurvedaPersonalHistory,
    ayurvedaDiagnosis,
    ayurvedaTreatment,
    isAyurveda,
    formattedDOB,
    genderDisplay,
    maritalStatusDisplay,
    formattedAddress,
  ]);

  const handleCopySummary = React.useCallback(async () => {
    const textToCopy = buildFullSummaryText();

    if (textToCopy) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        toast.success('Visit summary copied to clipboard', { duration: 2000 });
      } catch {
        toast.error('Failed to copy summary to clipboard');
      }
    }
  }, [buildFullSummaryText]);

  const handleDownloadSummary = React.useCallback(async () => {
    if (!selectedReport) {
      toast.error('No report selected');
      return;
    }

    try {
      // The Summary tab (no visit selected) shows the cross-visit AI
      // narrative (patient.visitSummary) when it exists, NOT selectedReport's
      // own data (that's only ever the most recent visit — see the matching
      // on-screen branch above). The download must match what's on screen.
      const isAggregateSummary = !selectedVisit && !!patient.visitSummary;

      // Build report data for PDF generation
      const reportData = isAggregateSummary
        ? {
            id: selectedReport.id,
            title: 'Overall Patient Summary',
            issued: patient.visitSummaryLastUpdated || undefined,
            conclusion: patient.visitSummary || undefined,
            patientName: patient.name,
            patientId: patient.identifier,
            vitals,
            isAggregate: true,
          }
        : {
            id: selectedReport.id,
            title: selectedReport.title || 'Consultation Report',
            issued: selectedReport.issued || undefined,
            conclusion: selectedReport.conclusion || undefined,
            status: selectedReport.status || undefined,
            patientName: patient.name,
            patientId: patient.identifier,
            practitionerName: selectedReport.practitionerName || undefined,
            vitals,
            // Single-report download — must be explicit, otherwise pdfGenerator's
            // reports.length - index fallback always computes "Visit 1".
            visitNumber: selectedVisit?.visitNumber,
          };

      // Generate PDF
      await generateDiagnosticReportsPDF([reportData], {
        name: patient.name,
        id: patient.identifier,
        dateOfBirth: patient.dateOfBirth,
        age: patient.age,
        gender: genderDisplay,
      });

      toast.success('PDF generated', { duration: 2000 });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF', { duration: 2000 });
    }
  }, [selectedReport, selectedVisit, patient, genderDisplay, vitals]);

  const handleCopy = () => {
    if (actions.onCopy) {
      actions.onCopy();
    } else {
      // Default: copy patient ID
      navigator.clipboard.writeText(patient.identifier);
    }
  };

  const handleDownload = () => {
    if (actions.onDownload) {
      actions.onDownload();
    }
  };

  if (error) {
    return (
      <div className="bg-[#E1E3ED]">
        <div className="py-5">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#E1E3ED]">
      <div className="absolute left-20 top-24">
        <button
          onClick={actions.onBack}
          className="flex h-[calc(48px*var(--app-scale))] w-[calc(48px*var(--app-scale))] items-center justify-center rounded-xl border border-[#EAEBF3] bg-[#EAEBF3] transition-colors hover:bg-[#D1DBFD]"
          aria-label="Go back"
        >
          <ChevronLeft className="h-[calc(32px*var(--app-scale))] w-[calc(32px*var(--app-scale))] text-[#717381]" />
        </button>
      </div>
      <div className="py-5">
        {/* Patient Info Card */}
        {loading ? (
          <div className="mb-3 flex h-[calc(140px*var(--app-scale))] items-center justify-center rounded-xl border border-white bg-white/90 shadow-sm backdrop-blur-[20px]">
            <div className="h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
          </div>
        ) : (
          <div className="mb-3 flex items-start gap-3 rounded-xl border border-white bg-white/90 p-3 shadow-sm backdrop-blur-[20px]">
            <div className="flex flex-1 items-start gap-3">
              {/* Avatar */}
              <div className="flex h-[calc(56px*var(--app-scale))] w-[calc(56px*var(--app-scale))] flex-shrink-0 items-center justify-center rounded-lg border border-[#E6E8EF] bg-[#47AFBA] text-[calc(24px*var(--app-scale))] font-normal text-[#E6E8EF]">
                {getInitials(patient.name)}
              </div>

              {/* Patient Info */}
              <div className="flex-1">
                <h1 className="font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(30px*var(--app-scale))] tracking-[-0.15px] text-gray-700">
                  {patient.name}
                </h1>

                {/* Divider */}
                <div className="my-2 h-px w-full bg-[#E6E8EF]" />

                {/* Patient Details Grid */}
                <div className="flex flex-wrap gap-3">
                  {patient.phoneNumber && (
                    <div className="w-[calc(200px*var(--app-scale))]">
                      <div className="text-[calc(14px*var(--app-scale))] font-medium leading-[calc(18px*var(--app-scale))] tracking-[-0.12px] text-[#717381]">
                        Phone number
                      </div>
                      <div className="text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#051858]">
                        {patient.phoneNumber}
                      </div>
                    </div>
                  )}
                  <div className="w-[calc(200px*var(--app-scale))]">
                    <div className="text-[calc(14px*var(--app-scale))] font-medium leading-[calc(18px*var(--app-scale))] tracking-[-0.12px] text-[#717381]">
                      Age/Gender
                    </div>
                    <div className="text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#051858]">
                      {patient.age}/{genderDisplay}
                    </div>
                  </div>
                  {typeof patient.weight === 'number' && (
                    <div className="w-[calc(200px*var(--app-scale))]">
                      <div className="text-[calc(14px*var(--app-scale))] font-medium leading-[calc(18px*var(--app-scale))] tracking-[-0.12px] text-[#717381]">
                        Weight
                      </div>
                      <div className="text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#051858]">
                        {patient.weight} kg
                      </div>
                    </div>
                  )}
                  <div className="w-[calc(200px*var(--app-scale))]">
                    <div className="text-[calc(14px*var(--app-scale))] font-medium leading-[calc(18px*var(--app-scale))] tracking-[-0.12px] text-[#717381]">
                      Date of Birth
                    </div>
                    <div className="text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#051858]">
                      {formattedDOB}
                    </div>
                  </div>
                  <div className="w-[calc(200px*var(--app-scale))]">
                    <div className="text-[calc(14px*var(--app-scale))] font-medium leading-[calc(18px*var(--app-scale))] tracking-[-0.12px] text-gray-700">
                      Marital Status
                    </div>
                    <div className="text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#051858]">
                      {maritalStatusDisplay}
                    </div>
                  </div>
                  <div className="min-w-[calc(572px*var(--app-scale))] flex-1">
                    <div className="text-[calc(14px*var(--app-scale))] font-medium leading-[calc(18px*var(--app-scale))] tracking-[-0.12px] text-[#717381]">
                      Address
                    </div>
                    <div className="text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#051858]">
                      {formattedAddress}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-shrink-0 items-center gap-3">
              {actions.onCopy && (
                <button
                  onClick={handleCopy}
                  className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-[calc(12px*var(--app-scale))] transition-colors hover:bg-[#D1DBFD]"
                  aria-label="Copy patient details"
                >
                  <Copy className="h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] text-[#6B8AF9]" />
                </button>
              )}
              {actions.onDownload && (
                <button
                  onClick={handleDownload}
                  className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-[calc(12px*var(--app-scale))] transition-colors hover:bg-[#D1DBFD]"
                  aria-label="Download records"
                >
                  <Download className="h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] text-[#6B8AF9]" />
                </button>
              )}
              {actions.onEdit && (
                <button
                  onClick={actions.onEdit}
                  className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-[calc(12px*var(--app-scale))] transition-colors hover:bg-[#D1DBFD]"
                  aria-label="Edit patient details"
                >
                  <Pencil className="h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] text-[#6B8AF9]" />
                </button>
              )}
              {actions.onStartConsultation && (
                <button
                  onClick={actions.onStartConsultation}
                  className="flex items-center gap-2 rounded-lg bg-[#2E3E6F] px-[calc(16px*var(--app-scale))] py-[calc(12px*var(--app-scale))] transition-colors hover:bg-[#2E3E6F]/90"
                >
                  <Mic className="h-[calc(18px*var(--app-scale))] w-[calc(18px*var(--app-scale))] text-white" />
                  <span className="text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-white">
                    Start consultation
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Three Column Layout Below Patient Card */}
        <div className="flex items-start gap-3">
          {/* Left Sidebar - Consultation History */}
          <div className="w-[240px] flex-shrink-0">
            <div className="flex max-h-[500px] flex-col gap-1 overflow-hidden py-3 pr-2">
              {/* Title */}
              <div className="px-3 py-2">
                <h2 className="text-xl font-medium leading-6 tracking-[0px] text-[#051858]">
                  Consultation History
                </h2>
              </div>

              {/* Tabs and Practitioner Filter */}
              {tabs && (
                <div className="px-3 py-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => tabs.onTabChange(VISIT_TABS.MINE)}
                      className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                        tabs.activeTab === VISIT_TABS.MINE
                          ? 'bg-[#061a61] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {tabs.myVisitsLabel}
                    </button>
                    <button
                      onClick={() => tabs.onTabChange(tabs.otherVisitsKey)}
                      className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                        tabs.activeTab === tabs.otherVisitsKey
                          ? 'bg-[#061a61] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {tabs.otherVisitsLabel}
                    </button>
                  </div>

                  {/* Practitioner Filter Dropdown (DMO only, shown on All Visits tab) */}
                  {practitionerFilter && tabs.activeTab === VISIT_TABS.ALL && (
                    <select
                      value={practitionerFilter.value || 'all'}
                      onChange={(e) =>
                        practitionerFilter.onChange(
                          e.target.value === 'all' ? undefined : e.target.value
                        )
                      }
                      className="mt-2 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
                    >
                      <option value="all">All Practitioners</option>
                      {practitionerFilter.options.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Divider */}
              <div className="h-px w-full bg-[#E6E8EF]" />

              {/* Visit List - Scrollable */}
              <div className="scrollbar-thin flex-1 space-y-1 overflow-y-auto py-1">
                {reports.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                  </div>
                ) : consultationHistory.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-[#717381]">No consultations yet</div>
                ) : (
                  <>
                    {/* Summary Tab */}
                    <button
                      onClick={() => reports.onSelect('')}
                      className={cn(
                        'w-full rounded-lg px-3 py-2 text-left transition-colors',
                        !selectedVisit ? 'bg-white' : 'hover:bg-white/50'
                      )}
                    >
                      <div className="text-base font-medium leading-6 tracking-[-0.16px] text-[#051858]">
                        Summary
                      </div>
                      <div className="text-sm leading-5 tracking-[-0.12px] text-[#717381]">
                        Updated on{' '}
                        {new Date()
                          .toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })
                          .replace(/\//g, '/')}
                      </div>
                    </button>

                    <div className="my-1 h-px w-full bg-[#E6E8EF]" />

                    {/* Visit Tabs */}
                    {consultationHistory.map((visit, index) => (
                      <React.Fragment key={visit.id}>
                        <button
                          onClick={() => reports.onSelect(visit.id)}
                          className={cn(
                            'w-full rounded-lg px-3 py-2 text-left transition-colors',
                            visit.isSelected ? 'bg-white' : 'hover:bg-white/50'
                          )}
                        >
                          <div className="text-base font-medium leading-6 tracking-[-0.16px] text-[#051858]">
                            Visit {visit.visitNumber}
                          </div>
                          <div className="text-sm leading-5 tracking-[-0.12px] text-[#717381]">
                            {visit.timeElapsed} -{' '}
                            {visit.date &&
                              new Date(visit.date)
                                .toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })
                                .replace(/\//g, '/')}
                          </div>
                          {visit.practitionerName && (
                            <div className="mt-1 text-sm leading-5 tracking-[-0.12px] text-[#6B8AF9]">
                              {visit.practitionerName}
                            </div>
                          )}
                        </button>
                        {index < consultationHistory.length - 1 && (
                          <div className="my-1 h-px w-full bg-[#E6E8EF]" />
                        )}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Center Column - Summary Section */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-2 rounded-xl border border-white bg-[#F5F6F9]/95 px-5 py-4 backdrop-blur-[15px]">
              <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-pranik-brand">
                {selectedVisit ? `Visit ${selectedVisit.visitNumber}` : 'Summary of all visits'}
              </h2>

              {/* Copy, Download and Edit icons */}
              <div className="flex gap-2">
                <button
                  onClick={handleCopySummary}
                  className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-3 transition-colors hover:bg-[#D1DBFD]"
                  aria-label="Copy summary"
                >
                  <Copy className="h-6 w-6 text-[#6B8AF9]" />
                </button>
                <button
                  onClick={handleDownloadSummary}
                  className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-3 transition-colors hover:bg-[#D1DBFD]"
                  aria-label="Download summary"
                >
                  <Download className="h-6 w-6 text-[#6B8AF9]" />
                </button>
                {isEditableVisit && actions.onSaveVisitEdit && !isEditingVisit && (
                  <button
                    onClick={handleStartEditVisit}
                    className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-3 transition-colors hover:bg-[#D1DBFD]"
                    aria-label="Edit visit"
                  >
                    <Pencil className="h-6 w-6 text-[#6B8AF9]" />
                  </button>
                )}
                {isEditingVisit && (
                  <div className="flex flex-1 justify-end gap-2">
                    <button
                      onClick={handleCancelEditVisit}
                      disabled={isSavingVisit}
                      className="rounded-lg border border-[#EAEBF3] bg-white px-4 py-2 text-sm font-medium text-[#586695] transition-colors hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveVisitEdit}
                      disabled={isSavingVisit}
                      className="rounded-lg bg-[#2E3E6F] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2E3E6F]/90 disabled:opacity-50"
                    >
                      {isSavingVisit ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              {/* Audio player — only show for a specific visit */}
              {selectedVisit && (
                <div className="flex items-center gap-3">
                  <button
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-[#6B8AF9] text-white hover:bg-[#5a7af8]"
                    aria-label="Play audio"
                  >
                    <Play className="h-4 w-4" />
                  </button>
                  <div className="flex-1">
                    <div className="h-1 w-full rounded-full bg-[#E6E8EF]">
                      <div className="h-1 w-0 rounded-full bg-[#6B8AF9]" />
                    </div>
                  </div>
                  <span className="text-xs text-[#717381]">02:04</span>
                </div>
              )}

              {/* Divider */}
              <div className="h-px w-full bg-[#E6E8EF]" />

              {/* Summary Content */}
              <div className="scrollbar-thin max-h-[400px] overflow-y-auto">
                {reports.detail.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                  </div>
                ) : isEditingVisit ? (
                  <div className="space-y-5">
                    {(formattedSummary || []).map((section, index) => {
                      const medKey = section.medicationsSourceKey;
                      return (
                        <div key={index}>
                          <p className="mb-2 font-semibold text-[#061A61]">{section.label}</p>
                          {medKey ? (
                            <div className="space-y-2">
                              {(editedMedications[medKey] || []).map((row, medIdx) => (
                                <div key={medIdx} className="flex flex-wrap items-center gap-2">
                                  <input
                                    value={row.name}
                                    onChange={(e) =>
                                      updateMedicationRow(medKey, medIdx, 'name', e.target.value)
                                    }
                                    placeholder="Generic name/Strength/Form"
                                    className={cn(EDIT_INPUT_CLASS, 'min-w-[180px] flex-[2]')}
                                  />
                                  <input
                                    value={row.dosage}
                                    onChange={(e) =>
                                      updateMedicationRow(medKey, medIdx, 'dosage', e.target.value)
                                    }
                                    placeholder="Dosage"
                                    className={cn(EDIT_INPUT_CLASS, 'min-w-[100px] flex-1')}
                                  />
                                  <input
                                    value={row.frequency}
                                    onChange={(e) =>
                                      updateMedicationRow(
                                        medKey,
                                        medIdx,
                                        'frequency',
                                        e.target.value
                                      )
                                    }
                                    placeholder="Schedule"
                                    className={cn(EDIT_INPUT_CLASS, 'min-w-[100px] flex-1')}
                                  />
                                  <input
                                    value={row.route}
                                    onChange={(e) =>
                                      updateMedicationRow(medKey, medIdx, 'route', e.target.value)
                                    }
                                    placeholder="Route"
                                    className={cn(EDIT_INPUT_CLASS, 'min-w-[90px] flex-1')}
                                  />
                                  <input
                                    value={row.duration}
                                    onChange={(e) =>
                                      updateMedicationRow(
                                        medKey,
                                        medIdx,
                                        'duration',
                                        e.target.value
                                      )
                                    }
                                    placeholder="Days"
                                    className={cn(EDIT_INPUT_CLASS, 'min-w-[80px] flex-1')}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeMedicationRow(medKey, medIdx)}
                                    aria-label="Remove medicine"
                                    className="flex-shrink-0 p-1.5 text-[#717381] hover:text-red-500"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addMedicationRow(medKey)}
                                className="flex items-center gap-1 text-base font-medium text-[#6B8AF9] hover:underline"
                              >
                                <Plus className="h-4 w-4" /> Add medicine
                              </button>
                            </div>
                          ) : section.diagnosisCodes && section.diagnosisCodes.length > 0 ? (
                            <div className="mt-2 w-full overflow-x-auto">
                              <table className="w-full min-w-[480px] border-collapse text-sm">
                                <thead>
                                  <tr className="border-b border-gray-200 text-left">
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Name
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Type
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Code
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {section.diagnosisCodes.map((item, codeIdx) => (
                                    <tr key={codeIdx} className="border-b border-gray-100">
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {item.symptom}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {item.type}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {item.description
                                          ? `${item.code} — ${item.description}`
                                          : item.code}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : section.fields && section.fields.length > 0 ? (
                            <div className="space-y-4">
                              {section.fields.map((field) =>
                                readOnlyStructuredKeys.has(field.sourceKey) ? (
                                  <div key={field.sourceKey}>
                                    <div
                                      className="text-sm text-[#586695]"
                                      dangerouslySetInnerHTML={{ __html: field.content }}
                                    />
                                    <p className="mt-1 text-xs text-[#717381]">
                                      This section can&apos;t be edited here.
                                    </p>
                                  </div>
                                ) : (
                                  <div key={field.sourceKey}>
                                    {section.fields!.length > 1 && (
                                      <label className="mb-1.5 block text-sm font-medium text-[#717381]">
                                        {field.label}
                                      </label>
                                    )}
                                    <textarea
                                      value={editedFields[field.sourceKey] ?? ''}
                                      onChange={(e) =>
                                        setEditedFields((prev) => ({
                                          ...prev,
                                          [field.sourceKey]: e.target.value,
                                        }))
                                      }
                                      rows={4}
                                      className={EDIT_TEXTAREA_CLASS}
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-[#8c91aa]">Not editable from here.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : !selectedVisit ? (
                  // Show patient visit summary when "Summary" tab is selected.
                  // This must prefer the backend's AI-aggregated, cross-visit
                  // narrative (patient.visitSummary) — the structured view
                  // below is only built from the single most-recently-selected
                  // report (see `selectedReport`/`formattedSummary` above), so
                  // showing it here instead would silently display just the
                  // latest visit's own fields and never actually "sum up" the
                  // patient's history. It's kept as a fallback only for the
                  // brief window before that narrative exists yet (e.g. right
                  // after the very first visit, before the background summary
                  // job finishes).
                  patient.visitSummary ? (
                    <>
                      <div
                        className="text-lg font-normal leading-7 tracking-[0px] text-[#586695] [&_li]:my-0.5 [&_p]:mb-1 [&_p]:mt-3 [&_p]:first:mt-0 [&_strong]:font-semibold [&_strong]:text-[#061A61] [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-10"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeClinicalHtml(
                            formatVisitSummaryText(patient.visitSummary)
                          ),
                        }}
                      />
                      {allergyAlertsSection}
                    </>
                  ) : showStructuredSummary && formattedSummary && formattedSummary.length > 0 ? (
                    <div className="space-y-4 text-lg font-normal leading-7 tracking-[0px] text-[#586695]">
                      {formattedSummary.map((section, index) => (
                        <div key={index}>
                          <p className="font-semibold text-[#061A61]">{section.label}:</p>
                          {section.medications && section.medications.length > 0 ? (
                            <div className="mt-2 w-full overflow-x-auto">
                              <table className="w-full min-w-[960px] border-collapse text-sm">
                                <thead>
                                  <tr className="border-b border-gray-200 text-left">
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Generic name/Strength/Form
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Composition
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Type
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Dosage
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Schedule
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Timing
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Route
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Days
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Relation with meal
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Comments
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Item Code
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {section.medications.map((med, medIdx) => (
                                    <tr key={medIdx} className="border-b border-gray-100">
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.name}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.composition || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.medicineType || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.dosage || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.frequency || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.timing || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.route || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.duration || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.relationWithMeal || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.comments || '—'}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {med.itemCode || '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : section.diagnosisCodes && section.diagnosisCodes.length > 0 ? (
                            <div className="mt-2 w-full overflow-x-auto">
                              <table className="w-full min-w-[480px] border-collapse text-sm">
                                <thead>
                                  <tr className="border-b border-gray-200 text-left">
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Name
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Type
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                      Code
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {section.diagnosisCodes.map((item, codeIdx) => (
                                    <tr key={codeIdx} className="border-b border-gray-100">
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {item.symptom}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {item.type}
                                      </td>
                                      <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                        {item.description
                                          ? `${item.code} — ${item.description}`
                                          : item.code}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p
                              className="whitespace-pre-line [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-4 [&_p]:my-0.5 [&_strong]:font-semibold [&_strong]:text-[#061A61] [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-10"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeClinicalHtml(section.content),
                              }}
                            />
                          )}
                        </div>
                      ))}
                      {allergyAlertsSection}
                    </div>
                  ) : consultationHistory.length > 0 ? (
                    // Visits exist but backend hasn't generated summary yet — show helpful fallback
                    (() => {
                      const lastVisitDate = consultationHistory[0]?.date;
                      const hasValidLastVisitDate =
                        Boolean(lastVisitDate) && !Number.isNaN(Date.parse(lastVisitDate));

                      return (
                        <div className="space-y-2">
                          <p className="text-sm text-[#586695]">
                            {consultationHistory.length === 1
                              ? '1 visit recorded. Summary is being generated — click Refresh to update.'
                              : `${consultationHistory.length} visits recorded. Summary is being generated — click Refresh to update.`}
                          </p>
                          {hasValidLastVisitDate && (
                            <p className="text-xs text-[#8c91aa]">
                              Last visit:{' '}
                              {new Date(lastVisitDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </p>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-lg leading-7 text-[#586695]">
                      No summary available. Complete a consultation to generate a summary.
                    </div>
                  )
                ) : formattedSummary && formattedSummary.length > 0 ? (
                  <div className="space-y-4 text-lg font-normal leading-7 tracking-[0px] text-[#586695]">
                    {formattedSummary.map((section, index) => (
                      <div key={index}>
                        <p className="font-semibold text-[#061A61]">{section.label}:</p>
                        {section.medications && section.medications.length > 0 ? (
                          <div className="mt-2 w-full overflow-x-auto">
                            <table className="w-full min-w-[960px] border-collapse text-sm">
                              <thead>
                                <tr className="border-b border-gray-200 text-left">
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Generic name/Strength/Form
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Type
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Dosage
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Schedule
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Route
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Days
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Relation with meal
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Comments
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Item Code
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {section.medications.map((med, medIdx) => (
                                  <tr key={medIdx} className="border-b border-gray-100">
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.name}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.medicineType || '—'}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.dosage || '—'}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.frequency || '—'}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.route || '—'}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.duration || '—'}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.relationWithMeal || '—'}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.comments || '—'}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {med.itemCode || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : section.diagnosisCodes && section.diagnosisCodes.length > 0 ? (
                          <div className="mt-2 w-full overflow-x-auto">
                            <table className="w-full min-w-[480px] border-collapse text-sm">
                              <thead>
                                <tr className="border-b border-gray-200 text-left">
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Name
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Type
                                  </th>
                                  <th className="whitespace-nowrap px-3 py-2.5 align-middle font-medium text-[#061A61]">
                                    Code
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {section.diagnosisCodes.map((item, codeIdx) => (
                                  <tr key={codeIdx} className="border-b border-gray-100">
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {item.symptom}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {item.type}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-[#586695]">
                                      {item.description
                                        ? `${item.code} — ${item.description}`
                                        : item.code}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p
                            className="whitespace-pre-line [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-4 [&_p]:my-0.5 [&_strong]:font-semibold [&_strong]:text-[#061A61] [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-10"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeClinicalHtml(section.content),
                            }}
                          />
                        )}
                      </div>
                    ))}
                    {allergyAlertsSection}
                  </div>
                ) : (
                  <div className="text-lg leading-7 text-[#586695]">
                    No consultation summary available. Start a consultation to generate a summary.
                  </div>
                )}
              </div>

              {/* Refresh button and last updated - only show for Summary tab */}
              {!selectedVisit && (
                <div className="flex items-center justify-between border-t border-[#E6E8EF] pt-3">
                  <span className="text-xs text-[#717381]">
                    {patient.visitSummaryLastUpdated
                      ? `Last updated: ${new Date(patient.visitSummaryLastUpdated).toLocaleString(
                          'en-GB',
                          {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}`
                      : ''}
                  </span>
                  {actions.onRefreshSummary && (
                    <button
                      onClick={actions.onRefreshSummary}
                      disabled={isRefreshingSummary}
                      className={cn(
                        'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                        isRefreshingSummary
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'bg-[#EEF6FF] text-[#6B8AF9] hover:bg-[#D1DBFD]'
                      )}
                    >
                      <RefreshCw size={14} className={isRefreshingSummary ? 'animate-spin' : ''} />
                      {isRefreshingSummary ? 'Refreshing...' : 'Refresh'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Dosha Profile, Medications and Investigations */}
          <div className="flex w-[400px] flex-shrink-0 flex-col gap-2">
            {/* Lab Reports Card */}
            <LabReportsSection patientId={patientId} />

            {/* Vitals Card */}
            <div className="flex flex-col gap-2 rounded-xl border border-white bg-[#F5F6F9]/95 px-5 py-4 backdrop-blur-[15px]">
              <div className="flex items-center justify-between">
                <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-pranik-brand">
                  Vitals
                </h2>
                {!isEditingVitals && vitalsEncounterId && actions.onSaveVitals && (
                  <button
                    onClick={handleStartEditVitals}
                    className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-2 transition-colors hover:bg-[#D1DBFD]"
                    aria-label="Edit vitals"
                  >
                    <Pencil className="h-4 w-4 text-[#6B8AF9]" />
                  </button>
                )}
              </div>
              <div className="h-px w-full bg-[#E6E8EF]" />
              <div className="scrollbar-thin max-h-[320px] overflow-y-auto">
                {isEditingVitals ? (
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#717381]">
                        Blood Pressure (mmHg)
                      </label>
                      <input
                        value={editedVitals.bloodPressure ?? ''}
                        onChange={(e) =>
                          setEditedVitals((prev) => ({ ...prev, bloodPressure: e.target.value }))
                        }
                        placeholder="120/80"
                        className={cn(EDIT_INPUT_CLASS, 'w-full text-lg')}
                      />
                    </div>
                    {NUMERIC_VITAL_FIELDS.map(({ key, label, unit }) => (
                      <div key={key}>
                        <label className="mb-1.5 block text-sm font-medium text-[#717381]">
                          {label} ({unit})
                        </label>
                        <input
                          type="number"
                          value={editedVitals[key] ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setEditedVitals((prev) => ({
                              ...prev,
                              [key]: raw === '' ? undefined : Number(raw),
                            }));
                          }}
                          className={cn(EDIT_INPUT_CLASS, 'w-full text-lg')}
                        />
                      </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        onClick={handleCancelEditVitals}
                        disabled={isSavingVitals}
                        className="rounded-lg border border-[#EAEBF3] bg-white px-4 py-2 text-sm font-medium text-[#586695] transition-colors hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveVitals}
                        disabled={isSavingVitals}
                        className="rounded-lg bg-[#2E3E6F] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2E3E6F]/90 disabled:opacity-50"
                      >
                        {isSavingVitals ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const formattedVitals = vitals ? formatVitals(vitals) : [];
                    if (isLoadingVitals) {
                      return (
                        <div className="flex items-center justify-center py-4">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                        </div>
                      );
                    }
                    if (formattedVitals.length > 0) {
                      return (
                        <div className="space-y-1">
                          {formattedVitals.map((vital, idx) => (
                            <p key={idx} className="text-lg font-normal leading-7 text-[#586695]">
                              {vital}
                            </p>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <div className="text-lg leading-7 text-[#586695]">No vitals recorded</div>
                    );
                  })()
                )}
              </div>
            </div>

            {/* Astha Sthaana Pareeksha Card - Ayurveda only */}
            {isAyurveda && (
              <div className="flex flex-col gap-2 rounded-xl border border-white bg-[#F5F6F9]/95 px-5 py-4 backdrop-blur-[15px]">
                <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-pranik-brand">
                  Astha Sthaana Pareeksha
                </h2>
                <div className="h-px w-full bg-[#E6E8EF]" />
                <div className="scrollbar-thin max-h-[180px] overflow-y-auto">
                  {reports.detail.isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                    </div>
                  ) : doshaProfileHtml ? (
                    <div
                      className="text-lg font-normal leading-7 tracking-[0px] text-[#586695] [&_li]:ml-5 [&_ul]:list-disc"
                      dangerouslySetInnerHTML={{ __html: sanitizeClinicalHtml(doshaProfileHtml) }}
                    />
                  ) : doshaProfile.length > 0 ? (
                    <ul className="list-disc space-y-0 pl-7 text-lg font-normal leading-7 tracking-[0px] text-[#586695]">
                      {doshaProfile.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-lg leading-7 text-[#586695]">
                      No astha sthaana pareeksha recorded
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Personal History Card - Ayurveda only */}
            {isAyurveda && (
              <div className="flex flex-col gap-2 rounded-xl border border-white bg-[#F5F6F9]/95 px-5 py-4 backdrop-blur-[15px]">
                <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-pranik-brand">
                  Personal History
                </h2>
                <div className="h-px w-full bg-[#E6E8EF]" />
                <div className="scrollbar-thin max-h-[180px] overflow-y-auto">
                  {reports.detail.isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                    </div>
                  ) : ayurvedaPersonalHistory.length > 0 ? (
                    <ul className="list-disc space-y-0 pl-7 text-lg font-normal leading-7 tracking-[0px] text-[#586695]">
                      {ayurvedaPersonalHistory.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-lg leading-7 text-[#586695]">
                      No personal history recorded
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Diagnosis Card (Ayurvedic/Allopathic) - Ayurveda only */}
            {isAyurveda && (
              <div className="flex flex-col gap-2 rounded-xl border border-white bg-[#F5F6F9]/95 px-5 py-4 backdrop-blur-[15px]">
                <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-pranik-brand">
                  Diagnosis
                </h2>
                <div className="h-px w-full bg-[#E6E8EF]" />
                <div className="scrollbar-thin max-h-[180px] overflow-y-auto">
                  {reports.detail.isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                    </div>
                  ) : ayurvedaDiagnosis.length > 0 ? (
                    <ul className="list-disc space-y-0 pl-7 text-lg font-normal leading-7 tracking-[0px] text-[#586695]">
                      {ayurvedaDiagnosis.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-lg leading-7 text-[#586695]">No diagnosis recorded</div>
                  )}
                </div>
              </div>
            )}

            {/* Treatment Card (Medicines/Treatment Principle/Given) - Ayurveda only */}
            {isAyurveda && (
              <div className="flex flex-col gap-2 rounded-xl border border-white bg-[#F5F6F9]/95 px-5 py-4 backdrop-blur-[15px]">
                <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-pranik-brand">
                  Treatment
                </h2>
                <div className="h-px w-full bg-[#E6E8EF]" />
                <div className="scrollbar-thin max-h-[260px] overflow-y-auto">
                  {reports.detail.isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                    </div>
                  ) : ayurvedaTreatment.medicinesTable.length > 0 ||
                    ayurvedaTreatment.medicinesText ||
                    ayurvedaTreatment.lines.length > 0 ||
                    ayurvedaTreatment.dietItems.length > 0 ||
                    ayurvedaTreatment.lifestyleItems.length > 0 ? (
                    <div className="flex flex-col gap-3 text-lg font-normal leading-7 tracking-[0px] text-[#586695]">
                      {ayurvedaTreatment.medicinesTable.length > 0 ? (
                        <MedicinesTable medicines={ayurvedaTreatment.medicinesTable} />
                      ) : ayurvedaTreatment.medicinesText ? (
                        <p>Medicines: {ayurvedaTreatment.medicinesText}</p>
                      ) : null}
                      {ayurvedaTreatment.lines.map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                      {/* Treatment Given — no text of its own, directly
                          contains its two nested sub-lists, Diet and
                          Lifestyle (same pattern as Diagnosis containing
                          Ayurvedic/Allopathic). */}
                      {(ayurvedaTreatment.dietItems.length > 0 ||
                        ayurvedaTreatment.lifestyleItems.length > 0) && (
                        <div>
                          <p className="font-medium text-pranik-brand">Treatment Given</p>
                          {ayurvedaTreatment.dietItems.length > 0 && (
                            <div className="mt-1">
                              <p className="font-medium">Diet</p>
                              <ul className="list-disc pl-6">
                                {ayurvedaTreatment.dietItems.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {ayurvedaTreatment.lifestyleItems.length > 0 && (
                            <div className="mt-1">
                              <p className="font-medium">Lifestyle</p>
                              <ul className="list-disc pl-6">
                                {ayurvedaTreatment.lifestyleItems.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-lg leading-7 text-[#586695]">No treatment recorded</div>
                  )}
                </div>
              </div>
            )}

            {/* Investigations Card */}
            <div className="flex flex-col gap-2 rounded-xl border border-white bg-[#F5F6F9]/95 px-5 py-4 backdrop-blur-[15px]">
              <div className="flex items-center justify-between">
                <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-pranik-brand">
                  Investigations
                </h2>
                {isInvestigationsEditable &&
                  actions.onSaveVisitEdit &&
                  !isEditingInvestigations && (
                    <button
                      onClick={handleStartEditInvestigations}
                      className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-2 transition-colors hover:bg-[#D1DBFD]"
                      aria-label="Edit investigations"
                    >
                      <Pencil className="h-4 w-4 text-[#6B8AF9]" />
                    </button>
                  )}
              </div>

              {/* Divider */}
              <div className="h-px w-full bg-[#E6E8EF]" />

              {/* Investigations List */}
              <div className="scrollbar-thin max-h-[220px] overflow-y-auto">
                {reports.detail.isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6b8af9] border-t-transparent" />
                  </div>
                ) : isEditingInvestigations ? (
                  <textarea
                    value={editedInvestigations}
                    onChange={(e) => setEditedInvestigations(e.target.value)}
                    placeholder="One investigation per line"
                    rows={4}
                    className={EDIT_TEXTAREA_CLASS}
                  />
                ) : investigations.length > 0 ? (
                  <ul className="list-disc space-y-0 pl-7 text-lg font-normal leading-7 tracking-[0px] text-[#586695]">
                    {investigations.map((investigation, index) => (
                      <li key={index}>{investigation}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-lg leading-7 text-[#586695]">No investigations recorded</div>
                )}
              </div>
              {isEditingInvestigations && (
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={handleCancelEditInvestigations}
                    disabled={isSavingInvestigations}
                    className="rounded-lg border border-[#EAEBF3] bg-white px-4 py-2 text-sm font-medium text-[#586695] transition-colors hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveInvestigations}
                    disabled={isSavingInvestigations}
                    className="rounded-lg bg-[#2E3E6F] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2E3E6F]/90 disabled:opacity-50"
                  >
                    {isSavingInvestigations ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>

            {/* Print Prescription Button */}
            <Button
              className="w-full bg-[#051858] text-white hover:bg-[#0a2470]"
              disabled={!prescriptionData}
              onClick={handlePrintPrescription}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print Prescription
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h3 className="mb-2 font-serif text-lg font-semibold">Confirm delete</h3>
            <p className="mb-4 text-sm text-gray-600">
              Are you sure you want to delete this patient record? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => deleteConfirm.onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteConfirm.onConfirm()}
                disabled={!!deleteConfirm.pending || deleteDisabled}
              >
                {deleteConfirm.pending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientRecordView;
